// ../../../se/work/tab5-pulse-array-smoke/browser/stevens-analysis.mjs
var HR_METHODS = [
  { id: "scipy", label: "SciPy-style", detail: "Prominence + refractory peak finder", lineage: "Stevens ref. 30 / scipy.signal.find_peaks", implementation: "WFE reconstruction; no SciPy code copied" },
  { id: "neurokit", label: "NeuroKit-style", detail: "Elgendi-inspired dual moving average", lineage: "Stevens refs. 32\u201333 / NeuroKit2 / Elgendi 2013", implementation: "WFE reconstruction; no NeuroKit2 code copied" },
  { id: "heartpy", label: "HeartPy-style", detail: "Rolling threshold + strict IBI rejection", lineage: "Stevens refs. 19 and 31 / HeartPy", implementation: "WFE reconstruction; no HeartPy code copied" }
];
var RR_METHODS = [
  { id: "vangent", label: "Van Gent", family: "HRV / EDR", lineage: "HeartPy 2019; NeuroKit2 ecg_rsp selector", implementation: "WFE interval-spectrum reconstruction" },
  { id: "soni", label: "Soni", family: "Peak amplitude variation", lineage: "Soni and Muniyandi 2019; NeuroKit2 ecg_rsp selector", implementation: "WFE amplitude-spectrum reconstruction" },
  { id: "charlton", label: "Charlton", family: "BW + AM + FM fusion", lineage: "Charlton et al. 2016; NeuroKit2 ecg_rsp selector", implementation: "WFE three-surrogate fusion reconstruction" },
  { id: "sarkar", label: "Sarkar", family: "Smoothed HRV / EDR", lineage: "Sarkar et al. 2015; NeuroKit2 ecg_rsp selector", implementation: "WFE smoothed-interval-spectrum reconstruction" },
  { id: "vallat", label: "Vallat", family: "Interpolated HRV spectrum", lineage: "Raphael Vallat 2018 community gist", implementation: "WFE rate-spectrum reconstruction; no gist code copied" }
];
var clamp = (value, low, high) => Math.max(low, Math.min(high, value));
function mean(values) {
  if (!values.length) return 0;
  let total = 0;
  for (let index = 0; index < values.length; index += 1) total += values[index];
  return total / values.length;
}
function median(values) {
  if (!values.length) return 0;
  const ordered = Array.from(values).sort((left, right) => left - right);
  const middle = Math.floor(ordered.length / 2);
  return ordered.length % 2 ? ordered[middle] : (ordered[middle - 1] + ordered[middle]) / 2;
}
function standardDeviation(values, average = mean(values)) {
  if (values.length < 2) return 0;
  let sum = 0;
  for (let index = 0; index < values.length; index += 1) {
    const delta = values[index] - average;
    sum += delta * delta;
  }
  return Math.sqrt(sum / values.length);
}
function movingAverage(values, windowSize) {
  const size = Math.max(1, Math.round(windowSize));
  const result = new Float32Array(values.length);
  let running = 0;
  for (let index = 0; index < values.length; index += 1) {
    running += values[index];
    if (index >= size) running -= values[index - size];
    result[index] = running / Math.min(size, index + 1);
  }
  return result;
}
function centeredMovingAverage(values, windowSize) {
  const size = Math.max(1, Math.round(windowSize));
  const half = Math.floor(size / 2);
  const prefix = new Float64Array(values.length + 1);
  for (let index = 0; index < values.length; index += 1) prefix[index + 1] = prefix[index] + values[index];
  const result = new Float32Array(values.length);
  for (let index = 0; index < values.length; index += 1) {
    const start = Math.max(0, index - half);
    const end = Math.min(values.length, index + half + 1);
    result[index] = (prefix[end] - prefix[start]) / (end - start);
  }
  return result;
}
function preprocessPpg(input, sampleRate) {
  const raw = input instanceof Float32Array ? input : Float32Array.from(input);
  if (!raw.length) return { raw, smooth: raw, baseline: raw, clean: raw, standardDeviation: 0, span: 0 };
  const smooth = centeredMovingAverage(raw, Math.max(3, sampleRate * 0.035));
  const baseline = centeredMovingAverage(smooth, Math.max(5, sampleRate * 0.75));
  const clean = new Float32Array(raw.length);
  let low = Infinity;
  let high = -Infinity;
  let total = 0;
  for (let index = 0; index < raw.length; index += 1) {
    clean[index] = smooth[index] - baseline[index];
    low = Math.min(low, raw[index]);
    high = Math.max(high, raw[index]);
    total += clean[index];
  }
  const average = total / clean.length;
  let variance = 0;
  for (let index = 0; index < clean.length; index += 1) {
    const delta = clean[index] - average;
    variance += delta * delta;
  }
  return {
    raw,
    smooth,
    baseline,
    clean,
    standardDeviation: Math.sqrt(variance / clean.length),
    span: high - low
  };
}
function enforceDistance(candidates, values, minimumDistance) {
  if (!candidates.length) return [];
  const selected = [];
  for (const candidate of candidates) {
    const previous = selected[selected.length - 1];
    if (previous === void 0 || candidate - previous >= minimumDistance) {
      selected.push(candidate);
    } else if (values[candidate] > values[previous]) {
      selected[selected.length - 1] = candidate;
    }
  }
  return selected;
}
function localProminence(values, index, radius) {
  let leftMinimum = values[index];
  let rightMinimum = values[index];
  for (let cursor = Math.max(0, index - radius); cursor < index; cursor += 1) leftMinimum = Math.min(leftMinimum, values[cursor]);
  for (let cursor = index + 1; cursor <= Math.min(values.length - 1, index + radius); cursor += 1) rightMinimum = Math.min(rightMinimum, values[cursor]);
  return values[index] - Math.max(leftMinimum, rightMinimum);
}
function scipyPeaks(processed, sampleRate) {
  const { clean, standardDeviation: deviation } = processed;
  const candidates = [];
  const minimumHeight = deviation * 0.12;
  const minimumProminence = deviation * 0.32;
  const radius = Math.max(2, Math.round(sampleRate * 0.18));
  for (let index = 2; index < clean.length - 2; index += 1) {
    if (clean[index] <= minimumHeight || clean[index] < clean[index - 1] || clean[index] <= clean[index + 1]) continue;
    if (localProminence(clean, index, radius) >= minimumProminence) candidates.push(index);
  }
  return enforceDistance(candidates, clean, Math.round(sampleRate * 0.3));
}
function neurokitPeaks(processed, sampleRate) {
  const { clean } = processed;
  const squared = new Float32Array(clean.length);
  let squaredMean = 0;
  for (let index = 0; index < clean.length; index += 1) {
    const positive = Math.max(0, clean[index]);
    squared[index] = positive * positive;
    squaredMean += squared[index];
  }
  squaredMean /= Math.max(1, squared.length);
  const peakAverage = centeredMovingAverage(squared, sampleRate * 0.111);
  const beatAverage = centeredMovingAverage(squared, sampleRate * 0.667);
  const minimumRegion = Math.max(3, Math.round(sampleRate * 0.08));
  const candidates = [];
  let regionStart = -1;
  for (let index = 0; index <= clean.length; index += 1) {
    const active = index < clean.length && peakAverage[index] > beatAverage[index] + squaredMean * 0.02;
    if (active && regionStart < 0) regionStart = index;
    if ((!active || index === clean.length) && regionStart >= 0) {
      const regionEnd = index - 1;
      if (regionEnd - regionStart + 1 >= minimumRegion) {
        let maximum = regionStart;
        for (let cursor = regionStart + 1; cursor <= regionEnd; cursor += 1) {
          if (clean[cursor] > clean[maximum]) maximum = cursor;
        }
        if (clean[maximum] > 0) candidates.push(maximum);
      }
      regionStart = -1;
    }
  }
  return enforceDistance(candidates, clean, Math.round(sampleRate * 0.3));
}
function heartpyPeaks(processed, sampleRate) {
  const { smooth, standardDeviation: deviation } = processed;
  const rolling = centeredMovingAverage(smooth, sampleRate * 0.75);
  const candidates = [];
  for (let index = 2; index < smooth.length - 2; index += 1) {
    const threshold = rolling[index] + deviation * 0.18;
    if (smooth[index] > threshold && smooth[index] >= smooth[index - 1] && smooth[index] > smooth[index + 1]) candidates.push(index);
  }
  const distanceFiltered = enforceDistance(candidates, smooth, Math.round(sampleRate * 0.3));
  if (distanceFiltered.length < 4) return distanceFiltered;
  const intervals = [];
  for (let index = 1; index < distanceFiltered.length; index += 1) intervals.push((distanceFiltered[index] - distanceFiltered[index - 1]) * 1e3 / sampleRate);
  const intervalMean = mean(intervals);
  const low = Math.max(333, intervalMean * 0.7, intervalMean - 300);
  const high = Math.min(1500, intervalMean * 1.3, intervalMean + 300);
  return distanceFiltered.filter((peak, index) => {
    if (index === 0) return true;
    const interval = (peak - distanceFiltered[index - 1]) * 1e3 / sampleRate;
    return interval >= low && interval <= high;
  });
}
function beatFeatures(peaks, processed, sampleRate) {
  const beats = [];
  const radius = Math.max(3, Math.round(sampleRate * 0.45));
  for (let index = 0; index < peaks.length; index += 1) {
    const peak = peaks[index];
    let localMinimum = processed.smooth[peak];
    for (let cursor = Math.max(0, peak - radius); cursor < peak; cursor += 1) localMinimum = Math.min(localMinimum, processed.smooth[cursor]);
    const intervalSeconds = index ? (peak - peaks[index - 1]) / sampleRate : null;
    beats.push({
      index: peak,
      time: peak / sampleRate,
      interval: intervalSeconds,
      heartRate: intervalSeconds ? 60 / intervalSeconds : null,
      amplitude: processed.smooth[peak] - localMinimum,
      baseline: processed.baseline[peak],
      peak: processed.smooth[peak]
    });
  }
  return beats;
}
function summarizeHeartRate(beats, durationSeconds) {
  const valid = beats.filter((beat) => beat.heartRate >= 40 && beat.heartRate <= 180);
  const recent = valid.filter((beat) => beat.time >= durationSeconds - 10);
  const source = recent.length >= 3 ? recent : valid;
  if (source.length < 2) return { value: null, instantaneous: null, confidence: 0, intervalCv: null };
  const rates = source.map((beat) => beat.heartRate);
  const intervals = source.map((beat) => beat.interval).filter(Boolean);
  const intervalAverage = mean(intervals);
  const intervalCv = intervalAverage ? standardDeviation(intervals, intervalAverage) / intervalAverage : 1;
  const coverage = clamp(source.length / Math.max(4, durationSeconds / 2), 0, 1);
  return {
    value: mean(rates),
    instantaneous: valid[valid.length - 1]?.heartRate ?? null,
    confidence: clamp((1 - intervalCv * 2.4) * 0.72 + coverage * 0.28, 0, 1),
    intervalCv
  };
}
function linearDetrend(values) {
  const result = new Float32Array(values.length);
  if (!values.length) return result;
  const center = (values.length - 1) / 2;
  const average = mean(values);
  let numerator = 0;
  let denominator = 0;
  for (let index = 0; index < values.length; index += 1) {
    const centered = index - center;
    numerator += centered * (values[index] - average);
    denominator += centered * centered;
  }
  const slope = numerator / (denominator || 1);
  for (let index = 0; index < values.length; index += 1) result[index] = values[index] - average - slope * (index - center);
  return result;
}
function resampleSeries(times, values, sampleRate = 4) {
  if (times.length < 4 || values.length !== times.length) return null;
  const start = times[0];
  const end = times[times.length - 1];
  if (end - start < 15) return null;
  const count = Math.floor((end - start) * sampleRate) + 1;
  const output = new Float32Array(count);
  let source = 0;
  for (let index = 0; index < count; index += 1) {
    const targetTime = start + index / sampleRate;
    while (source < times.length - 2 && times[source + 1] < targetTime) source += 1;
    const leftTime = times[source];
    const rightTime = times[source + 1];
    const fraction = clamp((targetTime - leftTime) / Math.max(1e-6, rightTime - leftTime), 0, 1);
    output[index] = values[source] + (values[source + 1] - values[source]) * fraction;
  }
  return { values: output, sampleRate, duration: end - start };
}
function respiratorySpectrum(times, values) {
  const resampled = resampleSeries(times, values, 4);
  if (!resampled) return { value: null, confidence: 0, peakRatio: 0, duration: 0 };
  const detrended = linearDetrend(resampled.values);
  const deviation = standardDeviation(detrended);
  if (deviation < 1e-4) return { value: null, confidence: 0, peakRatio: 0, duration: resampled.duration };
  let bestFrequency = 0;
  let bestPower = -Infinity;
  const powers = [];
  for (let frequency = 0.1; frequency <= 0.5001; frequency += 5e-3) {
    let real = 0;
    let imaginary = 0;
    for (let index = 0; index < detrended.length; index += 1) {
      const window2 = 0.5 - 0.5 * Math.cos(2 * Math.PI * index / Math.max(1, detrended.length - 1));
      const angle = 2 * Math.PI * frequency * index / resampled.sampleRate;
      const value = detrended[index] * window2;
      real += value * Math.cos(angle);
      imaginary -= value * Math.sin(angle);
    }
    const power = real * real + imaginary * imaginary;
    powers.push(power);
    if (power > bestPower) {
      bestPower = power;
      bestFrequency = frequency;
    }
  }
  const background = median(powers.filter((power) => power !== bestPower)) || 1e-9;
  const peakRatio = bestPower / background;
  const durationScore = clamp((resampled.duration - 15) / 15, 0, 1);
  const ratioScore = clamp((Math.log10(Math.max(1, peakRatio)) - 0.15) / 1.15, 0, 1);
  return {
    value: peakRatio >= 1.45 ? bestFrequency * 60 : null,
    confidence: ratioScore * (0.45 + durationScore * 0.55),
    peakRatio,
    duration: resampled.duration
  };
}
function filteredBeats(beats) {
  const valid = beats.filter((beat) => beat.interval >= 0.333 && beat.interval <= 1.5 && beat.heartRate >= 40 && beat.heartRate <= 180);
  if (valid.length < 4) return valid;
  const center = median(valid.map((beat) => beat.interval));
  return valid.filter((beat) => Math.abs(beat.interval - center) <= Math.max(0.3, center * 0.3));
}
function respiratoryMethods(beats) {
  const valid = filteredBeats(beats);
  const times = valid.map((beat) => beat.time);
  const intervals = valid.map((beat) => beat.interval);
  const amplitudes = valid.map((beat) => beat.amplitude);
  const baselines = valid.map((beat) => beat.baseline);
  const heartRates = valid.map((beat) => beat.heartRate);
  const vanGent = respiratorySpectrum(times, intervals);
  const soni = respiratorySpectrum(times, amplitudes);
  const smoothedIntervals = movingAverage(Float32Array.from(intervals), 3);
  const sarkar = respiratorySpectrum(times, Array.from(smoothedIntervals));
  const vallat = respiratorySpectrum(times, heartRates);
  const baselineWander = respiratorySpectrum(times, baselines);
  const amplitudeModulation = respiratorySpectrum(times, amplitudes);
  const frequencyModulation = respiratorySpectrum(times, intervals);
  const charltonInputs = [baselineWander, amplitudeModulation, frequencyModulation].filter((result) => result.value !== null);
  let charlton = { value: null, confidence: 0, peakRatio: 0, duration: 0, components: [] };
  if (charltonInputs.length) {
    const values = charltonInputs.map((result) => result.value);
    const center = median(values);
    const agreement = 1 - clamp(standardDeviation(values, mean(values)) / 6, 0, 1);
    charlton = {
      value: center,
      confidence: mean(charltonInputs.map((result) => result.confidence)) * (0.55 + agreement * 0.45),
      peakRatio: mean(charltonInputs.map((result) => result.peakRatio)),
      duration: Math.max(...charltonInputs.map((result) => result.duration)),
      components: [
        { id: "BW", value: baselineWander.value },
        { id: "AM", value: amplitudeModulation.value },
        { id: "FM", value: frequencyModulation.value }
      ]
    };
  }
  return { vangent: vanGent, soni, charlton, sarkar, vallat };
}
function peaksForMethod(method, processed, sampleRate) {
  if (method === "neurokit") return neurokitPeaks(processed, sampleRate);
  if (method === "heartpy") return heartpyPeaks(processed, sampleRate);
  return scipyPeaks(processed, sampleRate);
}
function analyzePpg(input, sampleRate = 500) {
  const samples = input instanceof Float32Array ? input : Float32Array.from(input);
  const durationSeconds = samples.length / sampleRate;
  const processed = preprocessPpg(samples, sampleRate);
  const methods = {};
  for (const method of HR_METHODS) {
    const peaks = peaksForMethod(method.id, processed, sampleRate);
    const beats = beatFeatures(peaks, processed, sampleRate);
    methods[method.id] = {
      ...summarizeHeartRate(beats, durationSeconds),
      peaks,
      beats,
      respiratory: respiratoryMethods(beats)
    };
  }
  const recentStart = Math.max(0, samples.length - sampleRate * 10);
  let low = Infinity;
  let high = -Infinity;
  let clipped = 0;
  for (let index = recentStart; index < samples.length; index += 1) {
    low = Math.min(low, samples[index]);
    high = Math.max(high, samples[index]);
    if (samples[index] <= 4 || samples[index] >= 4091) clipped += 1;
  }
  const recentCount = Math.max(1, samples.length - recentStart);
  return {
    sampleRate,
    sampleCount: samples.length,
    durationSeconds,
    processed,
    methods,
    signal: {
      span: Number.isFinite(low) ? high - low : 0,
      clippedFraction: clipped / recentCount,
      usable: durationSeconds >= 8 && processed.standardDeviation >= 2 && clipped / recentCount < 0.08
    },
    reconstruction: true
  };
}
function confidenceLabel(value) {
  if (value >= 0.72) return "HIGH";
  if (value >= 0.42) return "MED";
  if (value > 0) return "LOW";
  return "WAIT";
}

// ../../../se/work/tab5-pulse-array-smoke/browser/four-channel-ppg-protocol.mjs
var CHANNEL_COUNT = 4;
var FRAME_PREFIX = "PPG4";
var FRAME_VERSION = 1;
var METADATA_PREFIX = "PPG4_META";
var METADATA_REQUIRED_MESSAGE = "Waiting for valid UNO_R4_WIFI metadata before accepting PPG4 frames";
var EXPECTED_METADATA_LINE = "PPG4_META,1,UNO_R4_WIFI,A0|A1|A2|A3,500,12";
var UINT32_MAX = 4294967295;
var CANONICAL_UNSIGNED_DECIMAL = /^(?:0|[1-9][0-9]*)$/;
var DEFAULT_DEVICE_PROFILE = Object.freeze({
  id: "UNO_R4_WIFI",
  name: "Arduino UNO R4 WiFi",
  channelLabels: Object.freeze(["A0", "A1", "A2", "A3"]),
  channelDetails: Object.freeze(["analog input 0", "analog input 1", "analog input 2", "analog input 3"]),
  supply: "5V",
  safety: "Power every PulseSensor from 5V and share one GND.",
  sampleRate: 500,
  adcBits: 12
});
function crc16Ccitt(text) {
  const bytes = new TextEncoder().encode(text);
  let crc = 65535;
  for (const byte of bytes) {
    crc ^= byte << 8;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 32768 ? (crc << 1 ^ 4129) & 65535 : crc << 1 & 65535;
    }
  }
  return crc;
}
function parseFields({ line, version, sequence, baseMs, count, samplesHex, offsetsHex, reportedCrc }) {
  if (version !== FRAME_VERSION) throw new Error(`unsupported PPG4 version ${version}`);
  if (!Number.isSafeInteger(sequence) || sequence < 0 || sequence > UINT32_MAX) throw new Error("invalid sequence");
  if (!Number.isSafeInteger(baseMs) || baseMs < 0 || baseMs > UINT32_MAX) throw new Error("invalid base time");
  if (!Number.isInteger(count) || count < 1 || count > 50) throw new Error("invalid sample count");
  if (!/^[0-9a-f]+$/i.test(samplesHex) || samplesHex.length !== count * CHANNEL_COUNT * 3) throw new Error("invalid sample payload");
  if (!/^[0-9a-f]+$/i.test(offsetsHex) || offsetsHex.length !== count * 2) throw new Error("invalid offset payload");
  if (!Number.isInteger(reportedCrc)) throw new Error("invalid CRC field");
  const signedEnd = line.lastIndexOf(",");
  if (signedEnd < 0 || crc16Ccitt(line.slice(0, signedEnd)) !== reportedCrc) throw new Error("CRC mismatch");
  const samples = [];
  for (let sample = 0; sample < count; sample += 1) {
    const row = [];
    for (let channel = 0; channel < CHANNEL_COUNT; channel += 1) {
      const offset = (sample * CHANNEL_COUNT + channel) * 3;
      row.push(Number.parseInt(samplesHex.slice(offset, offset + 3), 16));
    }
    samples.push(row);
  }
  const offsets = Array.from({ length: count }, (_, index) => Number.parseInt(offsetsHex.slice(index * 2, index * 2 + 2), 16));
  if (offsets.some((value, index) => index > 0 && value <= offsets[index - 1])) {
    throw new Error("non-increasing offsets");
  }
  return { version, sequence, baseMs, count, samples, offsets };
}
function parsePpg4Frame(line) {
  const normalized = line;
  const fields = normalized.split(",");
  if (fields.length !== 8 || fields[0] !== FRAME_PREFIX) throw new Error("not a PPG4 frame");
  if (fields[1] !== String(FRAME_VERSION)) throw new Error("unsupported or non-canonical PPG4 version");
  if (![fields[2], fields[3], fields[4]].every((field) => CANONICAL_UNSIGNED_DECIMAL.test(field))) {
    throw new Error("non-canonical unsigned decimal field");
  }
  if (!/^[0-9a-fA-F]{4}$/.test(fields[7])) throw new Error("invalid CRC field");
  return parseFields({
    line: normalized,
    version: Number(fields[1]),
    sequence: Number(fields[2]),
    baseMs: Number(fields[3]),
    count: Number(fields[4]),
    samplesHex: fields[5],
    offsetsHex: fields[6],
    reportedCrc: Number.parseInt(fields[7], 16)
  });
}
function parsePpg4Metadata(line) {
  if (line !== EXPECTED_METADATA_LINE) {
    throw new Error("unsupported PPG4 metadata; expected UNO_R4_WIFI A0-A3 at 500 Hz/12-bit");
  }
  return DEFAULT_DEVICE_PROFILE;
}
var Ppg4MetadataHandshake = class {
  constructor() {
    this.reset();
  }
  reset() {
    this.validated = false;
    this.device = null;
    this.failed = false;
    this.error = METADATA_REQUIRED_MESSAGE;
  }
  accept(line) {
    if (this.failed) throw new Error(this.error);
    try {
      const device = parsePpg4Metadata(line);
      this.validated = true;
      this.device = device;
      this.error = "";
      return device;
    } catch (error) {
      this.revoke(error.message);
      throw error;
    }
  }
  revoke(message = METADATA_REQUIRED_MESSAGE) {
    this.validated = false;
    this.device = null;
    this.failed = true;
    this.error = message;
  }
  assertFrameAllowed() {
    if (!this.validated) throw new Error(this.error || METADATA_REQUIRED_MESSAGE);
  }
};

// ../../../se/work/tab5-pulse-array-smoke/browser/acquisition-evidence.mjs
var CONNECTION_UNKNOWN = "unknown";
var CONNECTION_CONNECTED = "connected";
var CONNECTION_FLOATING = "floating";
var UNO_TIMESTAMP_WRAP_MS = Math.ceil(2 ** 32 / 1e3);
var EXPECTED_SAMPLE_PERIOD_MS = 2;
var SAMPLE_PERIOD_TOLERANCE_MS = 1;
function isActiveSerialDisconnect(activePort, event) {
  return activePort !== null && activePort !== void 0 && event?.target === activePort;
}
function timestampWrapMsForDevice(deviceId) {
  if (deviceId === "UNO_R4_WIFI") return UNO_TIMESTAMP_WRAP_MS;
  return null;
}
function timestampTransition(previousSequence, previousMs, currentSequence, currentMs) {
  const sequenceStep = currentSequence - previousSequence;
  if (sequenceStep !== 1) {
    return { checked: false, discontinuity: false, backtrack: false, stepMs: null, errorMs: 0 };
  }
  const stepMs = currentMs - previousMs;
  const errorMs = Math.abs(stepMs - EXPECTED_SAMPLE_PERIOD_MS);
  return {
    checked: true,
    discontinuity: stepMs <= 0 || errorMs > SAMPLE_PERIOD_TOLERANCE_MS,
    backtrack: stepMs < 0,
    stepMs,
    errorMs
  };
}
function isAcquisitionReady({ serial, fresh, metadataValidated, evidence }) {
  if (!serial || !fresh || !metadataValidated || !evidence || evidence.frames < 1) return false;
  const cadencePlausible = Number.isFinite(evidence.deviceRateHz) && evidence.deviceRateHz >= 490 && evidence.deviceRateHz <= 510;
  return cadencePlausible && evidence.gaps === 0 && evidence.nonMonotonic === 0 && evidence.malformed === 0 && evidence.crcFailures === 0 && evidence.timestampDiscontinuities === 0 && evidence.timestampBacktracks === 0;
}
function declaredChannelConnections(channelLabels, declaration) {
  const unknown = channelLabels.map(() => CONNECTION_UNKNOWN);
  if (declaration === null) return { status: "absent", states: unknown, error: "" };
  const declared = declaration.split(",").map((label) => label.trim()).filter(Boolean);
  const valid = declared.length > 0 && new Set(declared).size === declared.length && declared.every((label) => channelLabels.includes(label));
  if (!valid) {
    return {
      status: "invalid",
      states: unknown,
      error: `sensorInputs declaration does not match ${channelLabels.join("/")}`
    };
  }
  const connected = new Set(declared);
  return {
    status: "declared",
    states: channelLabels.map((label) => connected.has(label) ? CONNECTION_CONNECTED : CONNECTION_FLOATING),
    error: ""
  };
}
function resetAcquisitionSession(state2, evidence, defaultDevice, declaration) {
  evidence.reset();
  state2.device = defaultDevice;
  state2.lastSequence = null;
  state2.totalSamples = 0;
  for (const channel of state2.channels) channel.length = 0;
  state2.deviceTimes.length = 0;
  state2.sequences.length = 0;
  state2.analysis = null;
  state2.analysisAt = 0;
  state2.lastPeakAbsolute = -1;
  state2.trends.length = 0;
  state2.transport.stream = false;
  state2.transport.freshAt = 0;
  state2.transport.lastBatchAt = 0;
  state2.transport.hz = 0;
  state2.transport.error = "";
  const connectionState = declaredChannelConnections(defaultDevice.channelLabels, declaration);
  state2.channelConnections = connectionState.states;
  state2.connectionDeclaration = connectionState;
}
var AcquisitionEvidence = class {
  constructor() {
    this.reset();
  }
  reset() {
    this.candidates = 0;
    this.frames = 0;
    this.timepoints = 0;
    this.malformed = 0;
    this.crcFailures = 0;
    this.gaps = 0;
    this.nonMonotonic = 0;
    this.timestampDiscontinuities = 0;
    this.timestampBacktracks = 0;
    this.timestampMaxErrorMs = 0;
    this.timestampStepMinMs = null;
    this.timestampStepMaxMs = null;
    this.firstHostMs = null;
    this.lastHostMs = null;
    this.firstFrameCount = 0;
    this.firstSequence = null;
    this.lastSequence = null;
    this.firstDeviceMs = null;
    this.lastDeviceMs = null;
    this.expectedNextSequence = null;
    this.previousRawDeviceMs = null;
    this.deviceTimestampEpochMs = 0;
    this.timestampWrapMs = UNO_TIMESTAMP_WRAP_MS;
    this.previousSampleSequence = null;
    this.previousDeviceMs = null;
  }
  noteCandidate() {
    this.candidates += 1;
  }
  reject(error) {
    this.malformed += 1;
    if (error?.message === "CRC mismatch") this.crcFailures += 1;
    if (error?.message === "non-increasing offsets") this.nonMonotonic += 1;
  }
  setDevice(deviceId) {
    const wrapMs = timestampWrapMsForDevice(deviceId);
    if (wrapMs === null) throw new Error(`unsupported acquisition device ${deviceId}`);
    this.timestampWrapMs = wrapMs;
  }
  unwrapDeviceTimestamp(rawMs) {
    const effectiveWrapMs = this.timestampWrapMs;
    if (this.previousRawDeviceMs !== null && rawMs < this.previousRawDeviceMs && this.previousRawDeviceMs - rawMs > effectiveWrapMs / 2) {
      this.deviceTimestampEpochMs += effectiveWrapMs;
    }
    this.previousRawDeviceMs = rawMs;
    return rawMs + this.deviceTimestampEpochMs;
  }
  accept(frame, hostMs) {
    const deviceTimes = frame.offsets.map((offset) => this.unwrapDeviceTimestamp(frame.baseMs + offset));
    for (let index = 0; index < deviceTimes.length; index += 1) {
      const sequence = frame.sequence + index;
      if (this.previousSampleSequence !== null && this.previousDeviceMs !== null) {
        const transition = timestampTransition(
          this.previousSampleSequence,
          this.previousDeviceMs,
          sequence,
          deviceTimes[index]
        );
        if (transition.checked) {
          this.timestampStepMinMs = this.timestampStepMinMs === null ? transition.stepMs : Math.min(this.timestampStepMinMs, transition.stepMs);
          this.timestampStepMaxMs = this.timestampStepMaxMs === null ? transition.stepMs : Math.max(this.timestampStepMaxMs, transition.stepMs);
          this.timestampMaxErrorMs = Math.max(this.timestampMaxErrorMs, transition.errorMs);
          if (transition.discontinuity) this.timestampDiscontinuities += 1;
          if (transition.backtrack) this.timestampBacktracks += 1;
        }
      }
      this.previousSampleSequence = sequence;
      this.previousDeviceMs = deviceTimes[index];
    }
    const firstDeviceMs = deviceTimes[0];
    const lastDeviceMs = deviceTimes[deviceTimes.length - 1];
    if (this.expectedNextSequence !== null) {
      if (frame.sequence > this.expectedNextSequence) this.gaps += frame.sequence - this.expectedNextSequence;
      else if (frame.sequence < this.expectedNextSequence) this.nonMonotonic += 1;
    }
    if (this.frames === 0) {
      this.firstHostMs = hostMs;
      this.firstFrameCount = frame.count;
      this.firstSequence = frame.sequence;
      this.firstDeviceMs = firstDeviceMs;
    }
    this.frames += 1;
    this.timepoints += frame.count;
    this.lastHostMs = hostMs;
    this.lastSequence = frame.sequence + frame.count - 1;
    this.lastDeviceMs = lastDeviceMs;
    this.expectedNextSequence = frame.sequence + frame.count;
  }
  snapshot() {
    const elapsedMs = this.firstHostMs === null || this.lastHostMs === null ? 0 : this.lastHostMs - this.firstHostMs;
    const sequenceSpan = this.firstSequence === null || this.lastSequence === null ? 0 : this.lastSequence - this.firstSequence;
    const deviceSpanMs = this.firstDeviceMs === null || this.lastDeviceMs === null ? 0 : this.lastDeviceMs - this.firstDeviceMs;
    return {
      candidates: this.candidates,
      frames: this.frames,
      timepoints: this.timepoints,
      malformed: this.malformed,
      crcFailures: this.crcFailures,
      gaps: this.gaps,
      nonMonotonic: this.nonMonotonic,
      timestampDiscontinuities: this.timestampDiscontinuities,
      timestampBacktracks: this.timestampBacktracks,
      timestampMaxErrorMs: this.timestampMaxErrorMs,
      timestampStepMinMs: this.timestampStepMinMs,
      timestampStepMaxMs: this.timestampStepMaxMs,
      elapsedMs,
      frameRateHz: elapsedMs > 0 ? (this.frames - 1) * 1e3 / elapsedMs : 0,
      hostTimepointRateHz: elapsedMs > 0 ? (this.timepoints - this.firstFrameCount) * 1e3 / elapsedMs : 0,
      deviceRateHz: deviceSpanMs > 0 ? sequenceSpan * 1e3 / deviceSpanMs : 0
    };
  }
};

// ../../../se/work/tab5-pulse-array-smoke/browser/recording-session.mjs
var BASE_COLUMNS = Object.freeze([
  "host_frame_received_at_ms",
  "device_timestamp_ms",
  "sequence"
]);
var TRAILING_COLUMNS = Object.freeze(["selected_channel", "posture"]);
var SESSION_ROW_WIDTH = BASE_COLUMNS.length + CHANNEL_COUNT + TRAILING_COLUMNS.length;
var MAX_ADC_VALUE = 2 ** DEFAULT_DEVICE_PROFILE.adcBits - 1;
var TERMINATION_REASON_CODES = /* @__PURE__ */ new Set([
  "manual_stop",
  "disconnect",
  "metadata_error",
  "stream_stale",
  "sender_fatal",
  "ra4m1_fatal",
  "usb_bridge_fatal",
  "maximum_duration"
]);
function channelColumn(label, index) {
  const suffix = label.toLowerCase().replaceAll(/[^a-z0-9]+/g, "_").replaceAll(/^_+|_+$/g, "");
  return `ch${index + 1}_${suffix}_raw`;
}
function csvCell(value) {
  const text = String(value ?? "");
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}
function validateDevice(device) {
  const labelsMatch = Array.isArray(device?.channelLabels) && device.channelLabels.length === CHANNEL_COUNT && device.channelLabels.every((label, index) => label === DEFAULT_DEVICE_PROFILE.channelLabels[index]);
  if (!labelsMatch || device.id !== DEFAULT_DEVICE_PROFILE.id || device.sampleRate !== DEFAULT_DEVICE_PROFILE.sampleRate || device.adcBits !== DEFAULT_DEVICE_PROFILE.adcBits) {
    throw new Error("recording export requires UNO_R4_WIFI A0-A3 at 500 Hz/12-bit");
  }
}
function validateTermination(termination) {
  if (!termination || !TERMINATION_REASON_CODES.has(termination.reasonCode) || typeof termination.detail !== "string") {
    throw new Error("recording requires a supported machine-readable termination reason and detail");
  }
}
function snapshotSelectedMethods(selectedMethods) {
  if (typeof selectedMethods?.heart !== "string" || !selectedMethods.heart || typeof selectedMethods?.respiratory !== "string" || !selectedMethods.respiratory) {
    throw new Error("recording requires selected heart and respiratory method identifiers");
  }
  return { heart: selectedMethods.heart, respiratory: selectedMethods.respiratory };
}
function validateSessionRow(row, index) {
  if (!Array.isArray(row) || row.length !== SESSION_ROW_WIDTH) {
    throw new Error(`recording row ${index} must contain ${SESSION_ROW_WIDTH} aligned fields`);
  }
  if (!Number.isSafeInteger(row[0]) || row[0] < 0 || !Number.isFinite(row[1]) || !Number.isSafeInteger(row[2]) || row[2] < 0) {
    throw new Error(`recording row ${index} has invalid timing or sequence fields`);
  }
  const channelValues = row.slice(BASE_COLUMNS.length, BASE_COLUMNS.length + CHANNEL_COUNT);
  if (channelValues.some((value) => !Number.isInteger(value) || value < 0 || value > MAX_ADC_VALUE)) {
    throw new Error(`recording row ${index} has invalid channel values`);
  }
  if (!Number.isInteger(row[BASE_COLUMNS.length + CHANNEL_COUNT]) || row[BASE_COLUMNS.length + CHANNEL_COUNT] < 1 || row[BASE_COLUMNS.length + CHANNEL_COUNT] > CHANNEL_COUNT) {
    throw new Error(`recording row ${index} has an invalid selected channel`);
  }
}
function validateAcquisitionEvidence(acquisitionEvidence2, sampleCount, timeline) {
  if (!acquisitionEvidence2 || typeof acquisitionEvidence2 !== "object") {
    throw new Error("recording export requires recording-scoped acquisition evidence");
  }
  const countFields = ["candidates", "frames", "timepoints", "malformed", "crcFailures"];
  for (const field of countFields) {
    if (!Number.isSafeInteger(acquisitionEvidence2[field]) || acquisitionEvidence2[field] < 0) {
      throw new Error(`recording evidence ${field} must be a nonnegative integer`);
    }
  }
  if (acquisitionEvidence2.timepoints !== sampleCount) {
    throw new Error(`recording evidence timepoints ${acquisitionEvidence2.timepoints} do not match ${sampleCount} exported samples`);
  }
  if (acquisitionEvidence2.frames < 1 || acquisitionEvidence2.frames > acquisitionEvidence2.timepoints) {
    throw new Error("recording evidence frame count is inconsistent with exported samples");
  }
  if (acquisitionEvidence2.candidates !== acquisitionEvidence2.frames + acquisitionEvidence2.malformed) {
    throw new Error("recording evidence candidates must equal accepted plus malformed frames");
  }
  if (acquisitionEvidence2.crcFailures > acquisitionEvidence2.malformed) {
    throw new Error("recording evidence CRC failures exceed malformed frames");
  }
  for (const field of ["gaps", "nonMonotonic", "timestampDiscontinuities", "timestampBacktracks"]) {
    if (!Number.isSafeInteger(acquisitionEvidence2[field]) || acquisitionEvidence2[field] < 0) {
      throw new Error(`recording evidence ${field} must be a nonnegative integer`);
    }
  }
  if (!Number.isFinite(acquisitionEvidence2.timestampMaxErrorMs) || acquisitionEvidence2.timestampMaxErrorMs < 0) {
    throw new Error("recording evidence timestampMaxErrorMs must be nonnegative");
  }
  for (const field of ["timestampStepMinMs", "timestampStepMaxMs"]) {
    if (acquisitionEvidence2[field] !== null && !Number.isFinite(acquisitionEvidence2[field])) {
      throw new Error(`recording evidence ${field} must be finite or null`);
    }
  }
  if (acquisitionEvidence2.gaps !== timeline.gaps) {
    throw new Error(`recording evidence gaps ${acquisitionEvidence2.gaps} do not match ${timeline.gaps} missing exported samples`);
  }
  for (const field of ["timestampDiscontinuities", "timestampBacktracks", "timestampMaxErrorMs", "timestampStepMinMs", "timestampStepMaxMs"]) {
    if (!Object.is(acquisitionEvidence2[field], timeline[field])) {
      throw new Error(`recording evidence ${field} does not match the exported timeline`);
    }
  }
}
function createRecordingTimestampState() {
  return {
    previousHostReceiptTimeMs: null,
    previousRawDeviceMs: null,
    deviceTimestampEpochMs: 0,
    previousSequence: null,
    previousDeviceTimestampMs: null
  };
}
function recordHostReceiptTime(hostReceiptTimeMs, timestampState) {
  if (!timestampState || !Object.hasOwn(timestampState, "previousHostReceiptTimeMs")) {
    throw new Error("recording batch requires recording-scoped host receipt state");
  }
  if (timestampState.previousHostReceiptTimeMs !== null && hostReceiptTimeMs < timestampState.previousHostReceiptTimeMs) {
    throw new Error("recording host frame receipt time moved backward");
  }
  timestampState.previousHostReceiptTimeMs = hostReceiptTimeMs;
}
function unwrapRecordingTimestamp(rawMs, timestampState) {
  if (!timestampState || !Object.hasOwn(timestampState, "previousRawDeviceMs") || !Object.hasOwn(timestampState, "deviceTimestampEpochMs")) {
    throw new Error("recording batch requires recording-scoped timestamp state");
  }
  if (timestampState.previousRawDeviceMs !== null && rawMs < timestampState.previousRawDeviceMs && timestampState.previousRawDeviceMs - rawMs > UNO_TIMESTAMP_WRAP_MS / 2) {
    timestampState.deviceTimestampEpochMs += UNO_TIMESTAMP_WRAP_MS;
  }
  timestampState.previousRawDeviceMs = rawMs;
  return rawMs + timestampState.deviceTimestampEpochMs;
}
function validateRecordingTransition(previousSequence, previousDeviceMs, sequence, deviceMs) {
  if (previousSequence === null || previousDeviceMs === null) return;
  const sequenceStep = sequence - previousSequence;
  if (!Number.isSafeInteger(sequenceStep) || sequenceStep <= 0) {
    throw new Error("recording sequence did not increase");
  }
  const deviceStepMs = deviceMs - previousDeviceMs;
  if (deviceStepMs <= 0) throw new Error("recording device timestamp moved backward or repeated");
  const expectedStepMs = sequenceStep * EXPECTED_SAMPLE_PERIOD_MS;
  if (Math.abs(deviceStepMs - expectedStepMs) > SAMPLE_PERIOD_TOLERANCE_MS) {
    throw new Error(`recording device timestamp continuity error: expected ${expectedStepMs} ms, received ${deviceStepMs} ms`);
  }
}
function validateExportTimeline(session) {
  const timeline = {
    gaps: 0,
    timestampDiscontinuities: 0,
    timestampBacktracks: 0,
    timestampMaxErrorMs: 0,
    timestampStepMinMs: null,
    timestampStepMaxMs: null
  };
  for (let index = 1; index < session.length; index += 1) {
    const previous = session[index - 1];
    const row = session[index];
    if (row[0] < previous[0]) throw new Error(`recording row ${index} has a backward host frame receipt time`);
    const sequenceStep = row[2] - previous[2];
    if (!Number.isSafeInteger(sequenceStep) || sequenceStep <= 0) {
      throw new Error(`recording row ${index} sequence did not increase`);
    }
    if (sequenceStep > 1) timeline.gaps += sequenceStep - 1;
    const deviceStepMs = row[1] - previous[1];
    if (deviceStepMs <= 0) throw new Error(`recording row ${index} device timestamp moved backward or repeated`);
    const expectedStepMs = sequenceStep * EXPECTED_SAMPLE_PERIOD_MS;
    if (Math.abs(deviceStepMs - expectedStepMs) > SAMPLE_PERIOD_TOLERANCE_MS) {
      throw new Error(`recording row ${index} device timestamp is inconsistent with its sequence`);
    }
    if (sequenceStep === 1) {
      const errorMs = Math.abs(deviceStepMs - EXPECTED_SAMPLE_PERIOD_MS);
      timeline.timestampMaxErrorMs = Math.max(timeline.timestampMaxErrorMs, errorMs);
      timeline.timestampStepMinMs = timeline.timestampStepMinMs === null ? deviceStepMs : Math.min(timeline.timestampStepMinMs, deviceStepMs);
      timeline.timestampStepMaxMs = timeline.timestampStepMaxMs === null ? deviceStepMs : Math.max(timeline.timestampStepMaxMs, deviceStepMs);
    }
  }
  return timeline;
}
function recordedRowsForBatch({ frame, hostReceiptTimeMs, selectedChannel, posture, timestampState }) {
  const { sequence, baseMs, count, samples, offsets } = frame;
  if (!Number.isSafeInteger(sequence) || sequence < 0 || !Number.isFinite(baseMs) || baseMs < 0 || !Number.isInteger(count) || count < 1 || samples?.length !== count || offsets?.length !== count) {
    throw new Error("recording batch metadata is inconsistent");
  }
  if (!Number.isInteger(selectedChannel) || selectedChannel < 1 || selectedChannel > CHANNEL_COUNT) {
    throw new Error("recording batch selected channel is invalid");
  }
  if (!Number.isSafeInteger(hostReceiptTimeMs) || hostReceiptTimeMs < 0) {
    throw new Error("recording batch host receipt time is invalid");
  }
  if (offsets.some((offset, index) => !Number.isInteger(offset) || offset < 0 || offset > 255 || index > 0 && offset <= offsets[index - 1])) {
    throw new Error("recording batch offsets are invalid or non-increasing");
  }
  samples.forEach((values, index) => {
    if (!Array.isArray(values) || values.length !== CHANNEL_COUNT || values.some((value) => !Number.isInteger(value) || value < 0 || value > MAX_ADC_VALUE)) {
      throw new Error(`recording batch sample ${index} is not four-channel aligned`);
    }
  });
  const nextTimestampState = { ...timestampState };
  recordHostReceiptTime(hostReceiptTimeMs, nextTimestampState);
  const rows = samples.map((values, index) => {
    const rawDeviceMs = baseMs + offsets[index];
    const deviceTimestampMs = unwrapRecordingTimestamp(rawDeviceMs, nextTimestampState);
    const sampleSequence = sequence + index;
    validateRecordingTransition(
      nextTimestampState.previousSequence,
      nextTimestampState.previousDeviceTimestampMs,
      sampleSequence,
      deviceTimestampMs
    );
    nextTimestampState.previousSequence = sampleSequence;
    nextTimestampState.previousDeviceTimestampMs = deviceTimestampMs;
    return [
      hostReceiptTimeMs,
      deviceTimestampMs,
      sampleSequence,
      ...values,
      selectedChannel,
      posture
    ];
  });
  Object.assign(timestampState, nextTimestampState);
  return rows;
}
function recordMethodSelection(state2, selectedMethods, changedAt, event = "selection_change") {
  if (!state2.recording) return false;
  if (!Number.isFinite(changedAt) || changedAt < state2.recordingStart) {
    throw new Error("recording method selection time is invalid");
  }
  const selection = snapshotSelectedMethods(selectedMethods);
  const previous = state2.recordingSelectedMethods;
  if (previous?.heart === selection.heart && previous?.respiratory === selection.respiratory) return false;
  state2.recordingSelectedMethods = selection;
  state2.methodSelectionHistory.push({
    event,
    atMs: changedAt,
    elapsedMs: changedAt - state2.recordingStart,
    heartMethodId: selection.heart,
    respiratoryMethodId: selection.respiratory
  });
  return true;
}
function startRecordingSegment(state2, startedAt) {
  if (!Number.isFinite(startedAt) || startedAt < 0) throw new Error("recording start time is invalid");
  state2.recording = true;
  state2.recordingStart = startedAt;
  state2.recordingStop = 0;
  state2.session = [];
  state2.summaries = [];
  state2.markers = [];
  state2.finalizedSegment = null;
  state2.recordingTimestampState = createRecordingTimestampState();
  state2.recordingSelectedMethods = null;
  state2.methodSelectionHistory = [];
  state2.lastSummaryMinute = -1;
  recordMethodSelection(state2, state2.selected, startedAt, "recording_start");
}
function immutableRecords(records) {
  return Object.freeze(records.map((record) => Object.freeze(Array.isArray(record) ? [...record] : { ...record })));
}
function finalizeRecordingSegment(state2, stoppedAt, acquisitionEvidence2 = {}, termination = { reasonCode: "manual_stop", detail: "" }) {
  if (!state2.recording) return state2.finalizedSegment || null;
  if (!Number.isFinite(stoppedAt) || stoppedAt < state2.recordingStart) throw new Error("recording stop time is invalid");
  validateTermination(termination);
  state2.recording = false;
  state2.recordingStop = stoppedAt;
  state2.finalizedSegment = Object.freeze({
    startedAt: state2.recordingStart,
    stoppedAt,
    session: immutableRecords(state2.session),
    summaries: immutableRecords(state2.summaries),
    markers: immutableRecords(state2.markers),
    acquisitionEvidence: Object.freeze({ ...acquisitionEvidence2 }),
    selectedMethods: Object.freeze({ ...state2.recordingSelectedMethods }),
    methodSelectionHistory: immutableRecords(state2.methodSelectionHistory),
    termination: Object.freeze({
      reasonCode: termination.reasonCode,
      detail: termination.detail
    })
  });
  return state2.finalizedSegment;
}
function resetRecordingSession(state2) {
  state2.recording = false;
  state2.recordingStart = 0;
  state2.recordingStop = 0;
  state2.session = [];
  state2.summaries = [];
  state2.markers = [];
  state2.finalizedSegment = null;
  state2.recordingTimestampState = createRecordingTimestampState();
  state2.recordingSelectedMethods = null;
  state2.methodSelectionHistory = [];
  state2.lastSummaryMinute = -1;
  if (state2.protocol) {
    state2.protocol.phase = 0;
    state2.protocol.running = false;
    state2.protocol.elapsed = 0;
    state2.protocol.startedAt = 0;
  }
}
function createSessionExport({
  session,
  device,
  selectedMethods,
  methodSelectionHistory = [],
  summaries = [],
  markers = [],
  heartMethods = [],
  respiratoryMethods: respiratoryMethods2 = [],
  connectionDeclaration = { status: "absent", error: "" },
  channelConnections = Array.from({ length: CHANNEL_COUNT }, () => "unknown"),
  acquisitionEvidence: acquisitionEvidence2 = {},
  recordingStartMs = null,
  recordingStopMs = null,
  termination = null,
  now = /* @__PURE__ */ new Date()
}) {
  validateDevice(device);
  if (!Array.isArray(session) || session.length === 0) throw new Error("recording export requires samples");
  session.forEach((row, index) => {
    validateSessionRow(row, index);
  });
  const timeline = validateExportTimeline(session);
  validateAcquisitionEvidence(acquisitionEvidence2, session.length, timeline);
  validateTermination(termination);
  if (!Array.isArray(channelConnections) || channelConnections.length !== CHANNEL_COUNT) {
    throw new Error(`recording export requires ${CHANNEL_COUNT} channel connection states`);
  }
  if (!(now instanceof Date) || !Number.isFinite(now.getTime())) throw new Error("recording export time is invalid");
  if (!Array.isArray(methodSelectionHistory)) throw new Error("recording method selection history must be an array");
  const columns = [
    ...BASE_COLUMNS,
    ...device.channelLabels.map(channelColumn),
    ...TRAILING_COLUMNS
  ];
  const csv = `${columns.join(",")}
${session.map((row) => row.map(csvCell).join(",")).join("\n")}`;
  const first = session[0];
  const last = session[session.length - 1];
  const manifest = {
    classification: "DEVELOPMENT",
    purpose: "research and educational",
    source: `${device.name} ${device.channelLabels.join("/")} PPG4 v1`,
    device,
    sampleRateHz: device.sampleRate,
    recording: {
      sampleCount: session.length,
      channelCount: CHANNEL_COUNT,
      columns,
      hostTimestampSemantics: "All rows from one PPG4 frame share the wall-clock millisecond captured when that complete frame was received; device_timestamp_ms is the per-sample timeline.",
      firstHostFrameReceivedAtMs: first[0],
      lastHostFrameReceivedAtMs: last[0],
      firstDeviceTimestampMs: first[1],
      lastDeviceTimestampMs: last[1],
      firstSequence: first[2],
      lastSequence: last[2],
      markerCount: markers.length,
      summaryCount: summaries.length,
      startedAtMs: recordingStartMs,
      stoppedAtMs: recordingStopMs,
      durationMs: Number.isFinite(recordingStartMs) && Number.isFinite(recordingStopMs) ? recordingStopMs - recordingStartMs : null,
      termination: {
        reasonCode: termination.reasonCode,
        detail: termination.detail
      }
    },
    acquisitionEvidence: acquisitionEvidence2,
    acquisitionEvidenceScope: "finalized recording segment only",
    wiringDeclaration: {
      status: connectionDeclaration.status,
      error: connectionDeclaration.error || "",
      channelStates: channelConnections,
      boundary: "Connection states are operator declarations; signal variation does not prove sensor presence."
    },
    paper: {
      authors: ["Guylian Stevens", "Luc Hantson", "Michiel Larmuseau", "Jan R. Heerman", "Vincent Siau", "Pascal Verdonck"],
      title: "A Guide to Measuring Heart and Respiratory Rates Based on Off-the-Shelf Photoplethysmographic Hardware and Open-Source Software",
      citation: "Stevens et al. (2024), Sensors 24(12):3766",
      doi: "10.3390/s24123766"
    },
    reconstructionBoundary: "Paper source, sampling rate, participant data, BLE format, and exact parameters were not published.",
    provenance: {
      document: "docs/PROVENANCE.md",
      implementation: "Original WFE JavaScript reconstruction; cited Python/package/gist source is not copied or imported.",
      heartMethods: heartMethods.map(({ id, lineage, implementation }) => ({ id, lineage, implementation })),
      respiratoryMethods: respiratoryMethods2.map(({ id, lineage, implementation }) => ({ id, lineage, implementation }))
    },
    selectedMethods: { ...selectedMethods },
    methodSelectionHistory: methodSelectionHistory.map((entry) => ({ ...entry })),
    summaries,
    markers
  };
  const stamp = now.toISOString().replaceAll(":", "-").replace(/\.\d{3}Z$/, "Z");
  return {
    csv: {
      filename: `four-channel-ppg-${stamp}.csv`,
      type: "text/csv;charset=utf-8",
      content: csv
    },
    manifest: {
      filename: `four-channel-ppg-manifest-${stamp}.json`,
      type: "application/json",
      content: JSON.stringify(manifest, null, 2),
      value: manifest
    }
  };
}

// ../../../se/work/tab5-pulse-array-smoke/browser/transport-health.mjs
var STREAM_STALE_AFTER_MS = 1500;
var STREAM_WATCHDOG_INTERVAL_MS = 250;
var PPG4_DEVICE_ERROR = /^PPG4_ERROR,UNO_R4_WIFI,([A-Z][A-Z0-9_]*)$/;
var RA4M1_FATAL_LINES = Object.freeze([
  /^Fault on thread .+$/,
  /^Fault on interrupt or bare metal\(no OS\) environment$/,
  /^Hard fault is caused by .+$/,
  /^Memory management fault is caused by .+$/,
  /^Bus fault is caused by .+$/,
  /^Usage fault is caused by .+$/,
  /^Debug fault is caused by .+$/,
  /^Assert on thread .+$/,
  /^Assert on interrupt or bare metal\(no OS\) environment$/,
  /^Error: Thread stack\([0-9a-fA-F]{8}\) was overflow$/,
  /^Error: Main stack\([0-9a-fA-F]{8}\) was overflow$/
]);
var ESP32S3_BRIDGE_FATAL_LINES = Object.freeze([
  /^Guru Meditation Error: Core\s+\d+ panic'ed \(.+\)\. Exception was unhandled\.$/,
  /^Core\s+\d+ panic'ed \(.+\)\. Exception was unhandled\.$/,
  /^Stack protection fault(?: in function .+)?$/,
  /^Task watchdog got triggered\.(?: .*)?$/,
  /^abort\(\) was called at PC 0x[0-9a-fA-F]+ on core \d+$/
]);
function classifyFatalSerialLine(line) {
  const deviceError = PPG4_DEVICE_ERROR.exec(line);
  if (deviceError) {
    return {
      source: "UNO R4 sender",
      cause: deviceError[1],
      line,
      message: `UNO R4 sender fault: ${line}`
    };
  }
  if (RA4M1_FATAL_LINES.some((pattern) => pattern.test(line))) {
    return { source: "RA4M1", cause: line, line, message: `RA4M1 fault: ${line}` };
  }
  if (ESP32S3_BRIDGE_FATAL_LINES.some((pattern) => pattern.test(line))) {
    return { source: "ESP32-S3 USB bridge", cause: line, line, message: `ESP32-S3 USB bridge fault: ${line}` };
  }
  return null;
}
function transportBadgeState({ serial, error, waitingForMetadata, live }) {
  if (!serial) return "offline";
  if (error && !waitingForMetadata) return "fault";
  return live ? "live" : "waiting";
}
function shouldFinalizeStaleRecording(event, recording) {
  return Boolean(event?.stale && recording);
}
var Ppg4StreamHealth = class {
  constructor({ staleAfterMs = STREAM_STALE_AFTER_MS } = {}) {
    if (!Number.isFinite(staleAfterMs) || staleAfterMs <= 0) throw new Error("invalid stream watchdog threshold");
    this.staleAfterMs = staleAfterMs;
    this.reset();
  }
  reset() {
    this.armedAt = null;
    this.lastFrameAt = null;
    this.live = false;
    this.stale = false;
    this.failed = false;
    this.error = "";
    this.fatal = null;
  }
  arm(now) {
    if (!Number.isFinite(now)) throw new Error("invalid stream watchdog time");
    if (this.armedAt === null) this.armedAt = now;
  }
  acceptFrame(now) {
    if (!Number.isFinite(now)) throw new Error("invalid frame receipt time");
    if (this.failed) throw new Error(this.error);
    const recovered = this.stale;
    this.arm(now);
    this.lastFrameAt = now;
    this.live = true;
    this.stale = false;
    if (recovered) this.error = "";
    return { recovered };
  }
  acceptSerialLine(line) {
    const fatal = classifyFatalSerialLine(line);
    if (!fatal) return { fatal: false, newlyFailed: false, finalizeRecording: false };
    const newlyFailed = !this.failed;
    if (newlyFailed) {
      this.failed = true;
      this.live = false;
      this.stale = false;
      this.fatal = fatal;
      this.error = fatal.message;
    }
    return {
      fatal: true,
      newlyFailed,
      finalizeRecording: newlyFailed,
      detail: this.fatal,
      message: this.error
    };
  }
  check(now, { serial, metadataValidated }) {
    if (!Number.isFinite(now)) throw new Error("invalid stream watchdog time");
    if (this.failed || !serial || !metadataValidated || this.armedAt === null) {
      return { stale: this.stale, becameStale: false, finalizeRecording: false, message: this.error };
    }
    const frameReference = this.lastFrameAt ?? this.armedAt;
    const ageMs = Math.max(0, now - frameReference);
    if (ageMs < this.staleAfterMs) {
      return { stale: false, becameStale: false, finalizeRecording: false, ageMs, message: this.error };
    }
    const becameStale = !this.stale;
    if (becameStale) {
      this.live = false;
      this.stale = true;
      this.error = `PPG4 stream stale: no valid frame for ${this.staleAfterMs} ms`;
    }
    return {
      stale: true,
      becameStale,
      finalizeRecording: becameStale,
      ageMs,
      message: this.error
    };
  }
};

// ../../../se/work/tab5-pulse-array-smoke/browser/dashboard.mjs
var SAMPLE_RATE = 500;
var ANALYSIS_SECONDS = 60;
var DISPLAY_SECONDS = 10;
var BUFFER_SECONDS = 180;
var MAX_SESSION_SECONDS = 30 * 60;
var SENSOR_INPUT_DECLARATION = new URLSearchParams(location.search).get("sensorInputs");
var $ = (selector, root = document) => root.querySelector(selector);
var $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
var clamp2 = (value, low, high) => Math.max(low, Math.min(high, value));
var formatValue = (value, digits = 0) => Number.isFinite(value) ? value.toFixed(digits) : "--";
var channelBuffers = Array.from({ length: 4 }, () => []);
var acquisitionEvidence = new AcquisitionEvidence();
var recordingEvidence = new AcquisitionEvidence();
var metadataHandshake = new Ppg4MetadataHandshake();
var streamHealth = new Ppg4StreamHealth();
var state = {
  device: DEFAULT_DEVICE_PROFILE,
  channels: channelBuffers,
  activeChannel: 0,
  raw: channelBuffers[0],
  deviceTimes: [],
  sequences: [],
  totalSamples: 0,
  lastSequence: null,
  analysis: null,
  analysisAt: 0,
  analysisBusy: false,
  transport: { serial: false, stream: false, freshAt: 0, lastBatchAt: 0, hz: 0, error: "" },
  channelConnections: ["unknown", "unknown", "unknown", "unknown"],
  connectionDeclaration: { status: "absent", error: "" },
  firmware: { bpm: 0, quality: 0, state: "STARTING", ibi: 0, amplitude: 0 },
  trends: [],
  selected: { heart: "neurokit", respiratory: "charlton" },
  lastPeakAbsolute: -1,
  recording: false,
  recordingStart: 0,
  recordingStop: 0,
  recordingTimestampState: createRecordingTimestampState(),
  finalizedSegment: null,
  recordingSelectedMethods: null,
  methodSelectionHistory: [],
  session: [],
  summaries: [],
  markers: [],
  lastSummaryMinute: -1,
  protocol: { phase: 0, running: false, elapsed: 0, startedAt: 0 }
};
var PROTOCOL = [
  { name: "Acclimation", kind: "SETTLE", seconds: 300, posture: "relaxed", instruction: "Sit comfortably, keep the dominant wrist relaxed, and let the signal stabilize before measurement." },
  { name: "Supine", kind: "MEASURE", seconds: 180, posture: "supine", instruction: "Lie still with the sensor wrist supported. Avoid talking or adjusting contact pressure." },
  { name: "Seated", kind: "MEASURE", seconds: 180, posture: "seated", instruction: "Sit upright with the sensor wrist relaxed and supported. Keep movement minimal." },
  { name: "Standing", kind: "MEASURE", seconds: 180, posture: "standing", instruction: "Stand comfortably with the sensor wrist quiet. Watch the quality gate for contact changes." },
  { name: "Walking in place", kind: "MEASURE", seconds: 180, posture: "walking", instruction: "Walk in place at a natural cadence. Expect motion artifacts; withheld estimates are valid outcomes." }
];
var PAPER_RESULTS = {
  supine: {
    label: "Supine",
    heartPyN: 7,
    rows: [
      ["HR", "SciPy", "\u2014", 5.73, "-17.31\u201328.77", 0.71],
      ["HR", "NeuroKit", "\u2014", 2.8, "-10.21\u201315.80", 0.83, true],
      ["HR", "HeartPy", "\u2014", -37.21, "-235.56\u2013161.15", 0.58],
      ["RR", "SciPy", "Van Gent", 2.45, "-2.99\u20137.88", 0.17],
      ["RR", "SciPy", "Soni", 2.5, "-3.70\u20138.69", 0.19],
      ["RR", "SciPy", "Charlton", 0.15, "-6.46\u20136.77", 0.48],
      ["RR", "SciPy", "Sarkar", -0.86, "-6.98\u20135.26", 0.41],
      ["RR", "SciPy", "Vallat", -2.14, "-13.81\u20139.53", 0.51],
      ["RR", "NeuroKit", "Van Gent", 3.36, "-2.57\u20139.30", 0.24],
      ["RR", "NeuroKit", "Soni", 4.51, "-1.79\u201310.81", 0.23],
      ["RR", "NeuroKit", "Charlton", 2.8, "-2.78\u20138.37", 0.4],
      ["RR", "NeuroKit", "Sarkar", 2.25, "-2.96\u20137.46", 0.36],
      ["RR", "NeuroKit", "Vallat", 4.03, "-3.12\u201311.18", 0.36],
      ["RR", "HeartPy", "Van Gent", 3.47, "-2.66\u20139.59", 0.22],
      ["RR", "HeartPy", "Soni", 4.21, "-1.72\u201310.13", 0.32],
      ["RR", "HeartPy", "Charlton", 1.5, "-4.90\u20137.90", 0.64],
      ["RR", "HeartPy", "Sarkar", 0.37, "-4.93\u20135.67", 0.68, true],
      ["RR", "HeartPy", "Vallat", 2.9, "-15.83\u201310.03", 0.75]
    ]
  },
  seated: {
    label: "Seated",
    heartPyN: 7,
    rows: [
      ["HR", "SciPy", "\u2014", 5.23, "-17.45\u201327.92", 0.55],
      ["HR", "NeuroKit", "\u2014", 0.59, "-8.64\u20139.81", 0.9, true],
      ["HR", "HeartPy", "\u2014", -3.65, "-46.46\u201339.15", 0.56],
      ["RR", "SciPy", "Van Gent", 1.82, "-1.52\u20135.16", 0.68],
      ["RR", "SciPy", "Soni", 1.81, "-3.35\u20136.97", 0.45],
      ["RR", "SciPy", "Charlton", 0.83, "-4.20\u20135.85", 0.61],
      ["RR", "SciPy", "Sarkar", -0.04, "-4.24\u20134.16", 0.67],
      ["RR", "SciPy", "Vallat", -0.96, "-8.27\u20136.35", 0.32],
      ["RR", "NeuroKit", "Van Gent", 2.31, "-1.81\u20136.42", 0.66],
      ["RR", "NeuroKit", "Soni", 3.02, "-1.10\u20137.14", 0.68],
      ["RR", "NeuroKit", "Charlton", 1.9, "-1.47\u20135.26", 0.82, true],
      ["RR", "NeuroKit", "Sarkar", 1.67, "-1.85\u20135.18", 0.79],
      ["RR", "NeuroKit", "Vallat", 2.88, "-1.29\u20137.04", 0.75],
      ["RR", "HeartPy", "Van Gent", 0.79, "-8.04\u20139.62", 0.42],
      ["RR", "HeartPy", "Soni", -0.31, "-12.46\u201311.84", -0.1],
      ["RR", "HeartPy", "Charlton", -1.37, "-13.90\u201311.16", -0.22],
      ["RR", "HeartPy", "Sarkar", -1.62, "-12.42\u20139.18", -0.1],
      ["RR", "HeartPy", "Vallat", -1.35, "-8.56\u20135.85", 0.45]
    ]
  },
  standing: {
    label: "Standing",
    heartPyN: 6,
    rows: [
      ["HR", "SciPy", "\u2014", 11.19, "-17.54\u201339.91", 0.51],
      ["HR", "NeuroKit", "\u2014", 1.1, "-18.97\u201321.18", 0.77, true],
      ["HR", "HeartPy", "\u2014", -15.29, "-114.71\u201384.14", 0.29],
      ["RR", "SciPy", "Van Gent", 3.29, "-2.28\u20138.85", 0.22],
      ["RR", "SciPy", "Soni", 1.97, "-5.05\u20138.99", 0.14],
      ["RR", "SciPy", "Charlton", -1.18, "-8.68\u20136.32", 0.39],
      ["RR", "SciPy", "Sarkar", -1.37, "-8.89\u20136.16", 0.32],
      ["RR", "SciPy", "Vallat", -5.61, "-18.14\u20136.92", 0.31],
      ["RR", "NeuroKit", "Van Gent", 4.18, "-1.77\u201310.14", 0.08],
      ["RR", "NeuroKit", "Soni", 4.68, "-1.35\u201310.71", 0.3],
      ["RR", "NeuroKit", "Charlton", 2.25, "-4.39\u20138.88", 0.47, true],
      ["RR", "NeuroKit", "Sarkar", 1.68, "-5.31\u20138.67", 0.29],
      ["RR", "NeuroKit", "Vallat", 4.76, "-3.55\u201313.07", 0.33],
      ["RR", "HeartPy", "Van Gent", 5.64, "-0.92\u201312.20", 0.04],
      ["RR", "HeartPy", "Soni", 5.13, "-3.84\u201314.11", -0.09],
      ["RR", "HeartPy", "Charlton", 0.54, "-12.09\u201313.17", 0.22],
      ["RR", "HeartPy", "Sarkar", 1.1, "-10.80\u201313.01", 0.26],
      ["RR", "HeartPy", "Vallat", -5.15, "-18.52\u20138.22", 0.3]
    ]
  },
  walking: {
    label: "Walking",
    heartPyN: 10,
    rows: [
      ["HR", "SciPy", "\u2014", 30.83, "-10.73\u201372.39", -0.47],
      ["HR", "NeuroKit", "\u2014", -1.58, "-23.92\u201320.76", 0.29, true],
      ["HR", "HeartPy", "\u2014", -42.15, "-265.18\u2013180.87", -0.53],
      ["RR", "SciPy", "Van Gent", 4.58, "-6.55\u201315.72", -0.16],
      ["RR", "SciPy", "Soni", 5.12, "-5.50\u201315.75", -0.05],
      ["RR", "SciPy", "Charlton", 4.77, "-6.15\u201315.69", -0.05],
      ["RR", "SciPy", "Sarkar", 3.89, "-7.15\u201314.92", -0.18],
      ["RR", "SciPy", "Vallat", 1.72, "-11.52\u201314.96", -0.07],
      ["RR", "NeuroKit", "Van Gent", 4.18, "-1.77\u201310.14", 0.23],
      ["RR", "NeuroKit", "Soni", 4.68, "-1.35\u201310.71", 0.31, true],
      ["RR", "NeuroKit", "Charlton", 2.25, "-4.39\u20138.88", 0.2],
      ["RR", "NeuroKit", "Sarkar", 1.68, "-5.31\u20138.67", 0],
      ["RR", "NeuroKit", "Vallat", null, "Not reported", null],
      ["RR", "HeartPy", "Van Gent", 4.52, "-6.01\u201315.05", 0.04],
      ["RR", "HeartPy", "Soni", 4.07, "-6.26\u201314.40", 0.17],
      ["RR", "HeartPy", "Charlton", -1.1, "-12.82\u201310.62", 0.29],
      ["RR", "HeartPy", "Sarkar", -0.14, "-12.12\u201311.83", 0.18],
      ["RR", "HeartPy", "Vallat", 1.41, "-11.64\u201314.47", 0.02]
    ]
  }
};
function showToast(message) {
  const toast = $("#toast");
  toast.textContent = message;
  toast.classList.add("show");
  clearTimeout(showToast.timer);
  showToast.timer = setTimeout(() => toast.classList.remove("show"), 2800);
}
function setTab(name, updateHash = true) {
  $$(".tab").forEach((button) => button.classList.toggle("is-active", button.dataset.tab === name));
  $$(".panel").forEach((panel) => panel.classList.toggle("is-active", panel.dataset.panel === name));
  if (updateHash) history.replaceState(null, "", `#${name}`);
  if (name === "live") requestAnimationFrame(drawCharts);
}
function initializeTabs() {
  $$(".tab").forEach((button) => button.addEventListener("click", () => setTab(button.dataset.tab)));
  const initial = location.hash.slice(1);
  if ($(`[data-panel="${initial}"]`)) setTab(initial, false);
}
function parseLine(line) {
  const fatalEvent = streamHealth.acceptSerialLine(line);
  if (fatalEvent.fatal) {
    metadataHandshake.revoke(fatalEvent.message);
    state.transport.error = fatalEvent.message;
    state.transport.stream = false;
    state.transport.hz = 0;
    if (fatalEvent.finalizeRecording) {
      const reasonCode = fatalEvent.detail.source === "UNO R4 sender" ? "sender_fatal" : fatalEvent.detail.source === "RA4M1" ? "ra4m1_fatal" : "usb_bridge_fatal";
      finalizeActiveRecording({
        message: "Device fault detected; recording segment finalized.",
        reasonCode,
        detail: fatalEvent.detail.line
      });
    }
    renderTransportEvidence();
    updateTransportBadge();
    return;
  }
  if (line.startsWith(METADATA_PREFIX)) {
    try {
      const device = metadataHandshake.accept(line);
      if (device.sampleRate !== SAMPLE_RATE) throw new Error(`dashboard requires ${SAMPLE_RATE} Hz metadata`);
      state.device = device;
      acquisitionEvidence.setDevice(device.id);
      streamHealth.arm(performance.now());
      state.transport.error = streamHealth.error;
      renderDeviceProfile();
    } catch (error) {
      metadataHandshake.revoke(error.message);
      state.transport.error = error.message;
      state.transport.stream = false;
      state.transport.hz = 0;
      finalizeActiveRecording({
        message: "Metadata validation failed; recording segment finalized.",
        reasonCode: "metadata_error",
        detail: error.message
      });
    }
    renderTransportEvidence();
    updateTransportBadge();
    return;
  }
  if (!line.startsWith("PPG4,")) return;
  try {
    metadataHandshake.assertFrameAllowed();
  } catch (error) {
    state.transport.error = error.message;
    state.transport.stream = false;
    state.transport.hz = 0;
    renderTransportEvidence();
    updateTransportBadge();
    return;
  }
  const receivedAt = performance.now();
  const hostReceiptTimeMs = Date.now();
  acquisitionEvidence.noteCandidate();
  if (state.recording) recordingEvidence.noteCandidate();
  let frame;
  try {
    frame = parsePpg4Frame(line);
  } catch (error) {
    acquisitionEvidence.reject(error);
    if (state.recording) recordingEvidence.reject(error);
    state.transport.error = error.message;
    renderTransportEvidence();
    return;
  }
  let recordingRows = null;
  if (state.recording) {
    try {
      recordingRows = recordedRowsForBatch({
        frame,
        hostReceiptTimeMs,
        selectedChannel: state.activeChannel + 1,
        posture: PROTOCOL[state.protocol.phase]?.posture || "unassigned",
        timestampState: state.recordingTimestampState
      });
    } catch (error) {
      acquisitionEvidence.accept(frame, receivedAt);
      recordingEvidence.reject(error);
      state.transport.error = error.message;
      renderTransportEvidence();
      throw error;
    }
  }
  acquisitionEvidence.accept(frame, receivedAt);
  if (state.recording) recordingEvidence.accept(frame, receivedAt);
  const healthEvent = streamHealth.acceptFrame(receivedAt);
  if (healthEvent.recovered) state.transport.error = "";
  addBatch(frame, receivedAt, recordingRows);
}
function checkStreamWatchdog() {
  const event = streamHealth.check(performance.now(), {
    serial: state.transport.serial,
    metadataValidated: metadataHandshake.validated
  });
  if (event.stale) {
    state.transport.stream = false;
    state.transport.hz = 0;
    state.transport.error = event.message;
    if (shouldFinalizeStaleRecording(event, state.recording)) finalizeActiveRecording({
      message: "PPG4 stream went stale; recording segment finalized.",
      reasonCode: "stream_stale",
      detail: event.message
    });
    if (event.becameStale) renderTransportEvidence();
  }
  updateTransportBadge();
}
function currentAcquisitionReady(evidence = acquisitionEvidence.snapshot(), now = performance.now()) {
  const transportFresh = state.transport.stream && !streamHealth.failed && !streamHealth.stale && now - state.transport.freshAt < streamHealth.staleAfterMs;
  return isAcquisitionReady({
    serial: state.transport.serial,
    fresh: transportFresh,
    metadataValidated: metadataHandshake.validated,
    evidence
  });
}
function renderDeviceProfile() {
  const declaration = declaredChannelConnections(state.device.channelLabels, SENSOR_INPUT_DECLARATION);
  state.channelConnections = declaration.states;
  state.connectionDeclaration = declaration;
  document.body.dataset.device = state.device.id;
  document.body.dataset.connectionDeclaration = declaration.status;
  document.body.dataset.channelConnections = declaration.states.join(",");
  $$(".channel[data-channel]").forEach((button, index) => {
    const connection = declaration.states[index];
    const connectionText = connection === CONNECTION_CONNECTED ? "PulseSensor connected (declared)" : connection === CONNECTION_FLOATING ? "Unconnected / floating (declared)" : "Connection unknown";
    $("strong", button).textContent = state.device.channelLabels[index];
    $("small", button).textContent = `${state.device.channelDetails[index]} \xB7 ${connectionText} \xB7 ${state.device.sampleRate} Hz`;
    button.dataset.connection = connection;
  });
  $("#waveformChannel").textContent = `CH${state.activeChannel + 1} / ${state.device.channelLabels[state.activeChannel]}`;
  $("#activeWiringBoard").textContent = state.device.name;
  $("#activeWiringSignals").textContent = declaration.status === "declared" ? state.device.channelLabels.map((label, index) => `${label} ${declaration.states[index] === CONNECTION_CONNECTED ? "connected" : "floating"}`).join(" \xB7 ") : state.device.channelLabels.join(" \xB7 ");
  $("#activeWiringPower").textContent = state.device.supply;
  $("#activeWiringDetail").textContent = `${state.device.adcBits}-bit \xB7 ${state.device.sampleRate} Hz \xB7 PPG4 v1`;
  $("#activeWiringSafety").textContent = state.device.safety;
  const connected = state.device.channelLabels.filter((_, index) => declaration.states[index] === CONNECTION_CONNECTED);
  const floating = state.device.channelLabels.filter((_, index) => declaration.states[index] === CONNECTION_FLOATING);
  $("#connectionDeclaration").textContent = declaration.status === "declared" ? `Bench declaration: ${connected.join(", ")} connected; ${floating.join(", ")} unconnected/floating. Signal variation does not prove sensor presence.` : declaration.error || "Connection status is undeclared. Signal variation does not prove sensor presence.";
  updateTransportBadge();
}
function addBatch({ sequence, baseMs, count, samples, offsets }, receivedAt, recordingRows) {
  if (state.lastSequence !== null && sequence !== state.lastSequence) {
    for (const channel of state.channels) channel.length = 0;
    state.deviceTimes.length = 0;
    state.sequences.length = 0;
    state.analysis = null;
  }
  state.lastSequence = sequence + count;
  for (let index = 0; index < count; index += 1) {
    const delta = offsets[index];
    const deviceTime = baseMs + delta;
    for (let channel = 0; channel < 4; channel += 1) state.channels[channel].push(samples[index][channel]);
    state.deviceTimes.push(deviceTime);
    state.sequences.push(sequence + index);
    state.totalSamples += 1;
    if (state.recording && state.session.length < MAX_SESSION_SECONDS * SAMPLE_RATE) {
      state.session.push(recordingRows[index]);
    }
  }
  if (state.recording && state.session.length >= MAX_SESSION_SECONDS * SAMPLE_RATE) {
    finalizeActiveRecording({
      message: "Maximum 30-minute recording reached; segment finalized.",
      reasonCode: "maximum_duration",
      detail: "Maximum 30-minute in-browser recording length reached"
    });
  }
  if (state.raw.length > BUFFER_SECONDS * SAMPLE_RATE) {
    const remove = state.raw.length - BUFFER_SECONDS * SAMPLE_RATE;
    for (const channel of state.channels) channel.splice(0, remove);
    state.deviceTimes.splice(0, remove);
    state.sequences.splice(0, remove);
  }
  const latestValues = samples[samples.length - 1];
  $$(".channel[data-channel]").forEach((button, channel) => {
    $("[data-channel-value]", button).textContent = `${latestValues[channel]} ADC`;
    button.dataset.value = String(latestValues[channel]);
  });
  document.body.dataset.channelValues = latestValues.join(",");
  if (state.transport.lastBatchAt) {
    const instantaneous = count * 1e3 / Math.max(1, receivedAt - state.transport.lastBatchAt);
    state.transport.hz = state.transport.hz ? state.transport.hz * 0.8 + instantaneous * 0.2 : instantaneous;
  }
  state.transport.lastBatchAt = receivedAt;
  state.transport.freshAt = receivedAt;
  state.transport.stream = true;
  renderTransportEvidence();
  scheduleAnalysis();
}
function scheduleAnalysis() {
  const now = performance.now();
  if (state.analysisBusy || now - state.analysisAt < 1800 || state.raw.length < SAMPLE_RATE * 8) return;
  state.analysisBusy = true;
  state.analysisAt = now;
  setTimeout(() => {
    try {
      const source = state.raw.slice(-ANALYSIS_SECONDS * SAMPLE_RATE);
      state.analysis = analyzePpg(Float32Array.from(source), SAMPLE_RATE);
      updateDetectedBeat(source.length);
      updateTrend();
      updateMinuteSummary();
      renderAll();
    } finally {
      state.analysisBusy = false;
    }
  }, 0);
}
function updateDetectedBeat(windowLength) {
  const method = state.analysis?.methods.neurokit;
  const peak = method?.peaks?.[method.peaks.length - 1];
  if (!Number.isFinite(peak)) return;
  const absolutePeak = state.totalSamples - windowLength + peak;
  if (absolutePeak > state.lastPeakAbsolute) {
    state.lastPeakAbsolute = absolutePeak;
    const pulse = $("#beatPulse");
    pulse.classList.remove("beat");
    requestAnimationFrame(() => pulse.classList.add("beat"));
  }
}
function selectedResults() {
  const heart = state.analysis?.methods[state.selected.heart];
  const respiratory = heart?.respiratory?.[state.selected.respiratory];
  return { heart, respiratory };
}
function updateTrend() {
  const { heart, respiratory } = selectedResults();
  const now = Date.now();
  state.trends.push({ time: now, heart: heart?.value ?? null, respiratory: respiratory?.value ?? null });
  state.trends = state.trends.filter((point) => now - point.time <= 6e4);
}
function updateMinuteSummary() {
  if (!state.recording) return;
  const minute = Math.floor((Date.now() - state.recordingStart) / 6e4);
  if (minute <= state.lastSummaryMinute || minute < 1) return;
  const { heart, respiratory } = selectedResults();
  state.summaries.push({
    minute,
    at: (/* @__PURE__ */ new Date()).toISOString(),
    posture: PROTOCOL[state.protocol.phase]?.posture || "unassigned",
    channel: state.activeChannel + 1,
    heartMethodId: state.recordingSelectedMethods.heart,
    respiratoryMethodId: state.recordingSelectedMethods.respiratory,
    heart: heart?.value ?? null,
    respiratory: respiratory?.value ?? null,
    quality: state.firmware.quality
  });
  state.lastSummaryMinute = minute;
}
function renderAll() {
  renderMetrics();
  renderMatrix();
  renderRecording();
  drawCharts();
}
function renderTransportEvidence() {
  const evidence = acquisitionEvidence.snapshot();
  state.transport.hz = evidence.hostTimepointRateHz;
  $("#feedRate").textContent = evidence.hostTimepointRateHz.toFixed(1);
  $("#deviceCadence").textContent = evidence.deviceRateHz.toFixed(1);
  $("#frameRate").textContent = evidence.frameRateHz.toFixed(1);
  $("#frameCount").textContent = evidence.frames.toLocaleString();
  $("#timepointCount").textContent = `${evidence.timepoints.toLocaleString()} timepoints \xB7 ${(evidence.elapsedMs / 1e3).toFixed(2)} s`;
  $("#gapCount").textContent = String(evidence.gaps);
  $("#malformedCount").textContent = String(evidence.malformed);
  $("#crcCount").textContent = String(evidence.crcFailures);
  $("#nonMonotonicCount").textContent = String(evidence.nonMonotonic);
  $("#timestampFaultCount").textContent = String(evidence.timestampDiscontinuities);
  $("#timestampBacktrackCount").textContent = String(evidence.timestampBacktracks);
  document.body.dataset.transportElapsedMs = String(Math.round(evidence.elapsedMs));
  document.body.dataset.transportFrames = String(evidence.frames);
  document.body.dataset.transportTimepoints = String(evidence.timepoints);
  document.body.dataset.transportFrameHz = evidence.frameRateHz.toFixed(2);
  document.body.dataset.transportHostHz = evidence.hostTimepointRateHz.toFixed(2);
  document.body.dataset.transportDeviceHz = evidence.deviceRateHz.toFixed(2);
  document.body.dataset.transportGaps = String(evidence.gaps);
  document.body.dataset.transportMalformed = String(evidence.malformed);
  document.body.dataset.transportCrc = String(evidence.crcFailures);
  document.body.dataset.transportNonMonotonic = String(evidence.nonMonotonic);
  document.body.dataset.transportTimestampFaults = String(evidence.timestampDiscontinuities);
  document.body.dataset.transportTimestampBacktracks = String(evidence.timestampBacktracks);
  document.body.dataset.transportTimestampMaxErrorMs = evidence.timestampMaxErrorMs.toFixed(2);
  document.body.dataset.transportTimestampStepMinMs = evidence.timestampStepMinMs === null ? "" : String(evidence.timestampStepMinMs);
  document.body.dataset.transportTimestampStepMaxMs = evidence.timestampStepMaxMs === null ? "" : String(evidence.timestampStepMaxMs);
  const acquisitionReady = currentAcquisitionReady(evidence);
  const streamStage = $('[data-stage="stream"]');
  streamStage.classList.toggle("ready", acquisitionReady);
  $("#stageStream").textContent = acquisitionReady ? "READY" : "WAIT";
  document.body.dataset.acquisitionReady = String(acquisitionReady);
  return evidence;
}
function renderMetrics() {
  const { heart, respiratory } = selectedResults();
  const signal = state.analysis?.signal;
  const usable = Boolean(signal?.usable && (heart?.confidence || 0) >= 0.42);
  const heartValue = usable ? heart?.value : null;
  const respiratoryValue = usable && (respiratory?.confidence || 0) >= 0.42 ? respiratory?.value : null;
  state.firmware.quality = Math.round((signal?.usable ? 7 : 0) + Math.min(1, heart?.confidence || 0) * 5);
  state.firmware.state = usable ? "USABLE" : state.analysis ? "CHECK_SIGNAL" : "ACQUIRING";
  $("#heartRateValue").textContent = formatValue(heartValue);
  $("#respRateValue").textContent = formatValue(respiratoryValue);
  $("#heartConfidence").textContent = usable ? confidenceLabel(heart?.confidence || 0) : "GATED";
  $("#respConfidence").textContent = usable ? confidenceLabel(respiratory?.confidence || 0) : "GATED";
  $("#respWindow").textContent = state.analysis ? `${Math.min(60, Math.round(state.analysis.durationSeconds))} s evidence` : "Needs \u2265 20 s";
  $("#qualityValue").textContent = String(state.firmware.quality);
  $("#qualityState").textContent = state.firmware.state.replaceAll("_", " ");
  $("#qualityBar").style.width = `${state.firmware.quality / 12 * 100}%`;
  $("#signalSpan").textContent = signal ? `Span ${Math.round(signal.span)} ADC` : "Span --";
  $("#clipState").textContent = signal?.clippedFraction > 0.01 ? `${(signal.clippedFraction * 100).toFixed(1)}% clipped` : "No clipping";
  renderTransportEvidence();
  const stages = [
    ["clean", Boolean(state.analysis?.signal?.usable)],
    ["heart", usable && Number.isFinite(heart?.value)],
    ["resp", usable && Number.isFinite(respiratory?.value)],
    ["minute", state.analysis?.durationSeconds >= 60]
  ];
  for (const [stage, ready] of stages) {
    const item = $(`[data-stage="${stage}"]`);
    item.classList.toggle("ready", ready);
    $(`#stage${stage[0].toUpperCase()}${stage.slice(1)}`).textContent = ready ? "READY" : "WAIT";
  }
  updateTransportBadge();
  document.body.dataset.live = usable ? "true" : "false";
  document.body.dataset.heartRate = Number.isFinite(heartValue) ? heartValue.toFixed(1) : "";
  document.body.dataset.respiratoryRate = Number.isFinite(respiratoryValue) ? respiratoryValue.toFixed(1) : "";
  document.body.dataset.quality = String(state.firmware.quality);
  document.body.dataset.transportHz = state.transport.hz.toFixed(1);
  document.body.dataset.timepoints = String(state.totalSamples);
  document.body.dataset.analysisBoundary = "browser reconstructions; paper parameters and study data unpublished";
}
function updateTransportBadge() {
  const fresh = state.transport.stream && !streamHealth.stale && !streamHealth.failed && performance.now() - state.transport.freshAt < streamHealth.staleAfterMs;
  const badge = $("#transportBadge");
  const waitingForMetadata = state.transport.serial && !metadataHandshake.validated && !metadataHandshake.failed && state.transport.error === metadataHandshake.error;
  const status = transportBadgeState({
    serial: state.transport.serial,
    error: state.transport.error,
    waitingForMetadata,
    live: state.transport.stream && fresh
  });
  let title = "UNO R4 disconnected";
  let detail = state.transport.error || "UNO R4 WiFi \xB7 A0\u2013A3 \xB7 5V \xB7 500 Hz";
  if (status === "fault") {
    title = streamHealth.failed ? "UNO R4 fault \u2014 reconnect required" : streamHealth.stale ? "UNO R4 stream stale" : "UNO R4 transport error";
  } else if (status === "live") {
    title = `${state.device.name} \xB7 four channels`;
    detail = `${state.transport.hz.toFixed(1)} timepoints/s \xB7 CH${state.activeChannel + 1} selected`;
  } else if (status === "waiting") {
    title = metadataHandshake.validated ? `${state.device.name} attached` : "UNO R4 metadata required";
    detail = state.transport.error || "Waiting for full-rate frames";
  }
  badge.dataset.state = status;
  $("#transportTitle").textContent = title;
  $("#transportDetail").textContent = detail;
}
function renderMatrix() {
  const matrix = $("#methodMatrix");
  const analysis = state.analysis;
  const cells = [];
  cells.push('<div class="matrix-cell header corner" role="columnheader">HEART SOURCE \u2193 \xB7 RR METHOD \u2192</div>');
  for (const respiratory of RR_METHODS) cells.push(`<div class="matrix-cell header" role="columnheader"><strong>${respiratory.label}</strong><small>${respiratory.family}</small></div>`);
  for (const method of HR_METHODS) {
    const result = analysis?.methods[method.id];
    cells.push(`<div class="matrix-cell row-head" role="rowheader"><strong>${method.label}</strong><small>${method.detail}</small><b>${formatValue(result?.value)} <span>BPM</span></b></div>`);
    for (const respiratory of RR_METHODS) {
      const estimate = result?.respiratory?.[respiratory.id];
      const selected = state.selected.heart === method.id && state.selected.respiratory === respiratory.id;
      const confidence = estimate?.confidence || 0;
      cells.push(`<button class="matrix-cell result${selected ? " selected" : ""}" type="button" role="cell" data-heart="${method.id}" data-respiratory="${respiratory.id}"><b>${formatValue(estimate?.value)} <span>BrPM</span></b><small>${confidenceLabel(confidence)} \xB7 ${estimate?.duration ? Math.round(estimate.duration) : 0} s</small><span class="confidence"><i style="width:${confidence * 100}%"></i></span></button>`);
    }
  }
  matrix.innerHTML = cells.join("");
  $$(".matrix-cell.result", matrix).forEach((button) => button.addEventListener("click", () => {
    state.selected.heart = button.dataset.heart;
    state.selected.respiratory = button.dataset.respiratory;
    recordMethodSelection(state, state.selected, Date.now());
    renderAll();
  }));
  $("#matrixWindow").textContent = analysis ? `${Math.round(analysis.durationSeconds)} s \xB7 ${analysis.sampleCount.toLocaleString()} samples` : "Waiting for signal";
  renderMethodSummary();
}
function renderMethodSummary() {
  const heartDefinition = HR_METHODS.find((method) => method.id === state.selected.heart);
  const respiratoryDefinition = RR_METHODS.find((method) => method.id === state.selected.respiratory);
  const { heart, respiratory } = selectedResults();
  const componentText = respiratory?.components?.length ? respiratory.components.map((component) => `${component.id} ${formatValue(component.value)}`).join(" \xB7 ") : respiratoryDefinition?.family;
  $("#methodSummary").innerHTML = `<p class="eyebrow">Selected live combination</p><h2>${heartDefinition.label}<br>+ ${respiratoryDefinition.label}</h2><dl><div><dt>Analysis channel</dt><dd>CH${state.activeChannel + 1}</dd></div><div><dt>Heart rate</dt><dd>${formatValue(heart?.value)} BPM</dd></div><div><dt>Respiration</dt><dd>${formatValue(respiratory?.value)} BrPM</dd></div><div><dt>Heart confidence</dt><dd>${confidenceLabel(heart?.confidence || 0)}</dd></div><div><dt>RR confidence</dt><dd>${confidenceLabel(respiratory?.confidence || 0)}</dd></div><div><dt>RR evidence</dt><dd>${componentText || "Waiting"}</dd></div></dl><p class="microcopy">Each channel stays independent. The Live Lab withholds low-confidence estimates and never auto-fuses sensors.</p>`;
  $("#heartMethodLabel").textContent = heartDefinition.label;
  $("#respMethodLabel").textContent = respiratoryDefinition.id === "charlton" ? "Charlton fusion" : respiratoryDefinition.label;
}
function canvasContext(canvas) {
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  const ratio = Math.min(2, window.devicePixelRatio || 1);
  const width = Math.round(rect.width * ratio);
  const height = Math.round(rect.height * ratio);
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  const context = canvas.getContext("2d");
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  return { context, width: rect.width, height: rect.height };
}
function drawGrid(context, width, height, columns = 10, rows = 4) {
  context.strokeStyle = "rgba(205,231,216,.075)";
  context.lineWidth = 1;
  for (let column = 1; column < columns; column += 1) {
    const x = width * column / columns;
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x, height);
    context.stroke();
  }
  for (let row = 1; row < rows; row += 1) {
    const y = height * row / rows;
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(width, y);
    context.stroke();
  }
}
function drawWaveform() {
  const canvas = $("#waveformCanvas");
  const target = canvasContext(canvas);
  if (!target) return;
  const { context, width, height } = target;
  context.fillStyle = "#07110f";
  context.fillRect(0, 0, width, height);
  drawGrid(context, width, height);
  const count = Math.min(DISPLAY_SECONDS * SAMPLE_RATE, state.raw.length);
  if (count < 10) {
    context.fillStyle = "rgba(210,231,220,.38)";
    context.font = "11px DM Mono, monospace";
    context.textAlign = "center";
    context.fillText(`WAITING FOR REAL CH${state.activeChannel + 1} SAMPLES`, width / 2, height / 2);
    return;
  }
  const raw = state.raw.slice(-count);
  const low = Math.min(...raw);
  const high = Math.max(...raw);
  const range = Math.max(1, high - low);
  const analysis = state.analysis;
  const processed = analysis?.processed;
  const processedOffset = processed ? Math.max(0, processed.raw.length - count) : 0;
  const step = Math.max(1, Math.floor(count / Math.max(1, width * 1.8)));
  const drawSeries = (color, lineWidth, accessor, alpha = 1) => {
    context.beginPath();
    context.strokeStyle = color;
    context.globalAlpha = alpha;
    context.lineWidth = lineWidth;
    let drawn = 0;
    for (let index = 0; index < count; index += step) {
      const x = index / (count - 1) * width;
      const value = accessor(index);
      const y = height - 18 - (value - low) / range * (height - 36);
      if (!drawn) context.moveTo(x, y);
      else context.lineTo(x, y);
      drawn += 1;
    }
    context.stroke();
    context.globalAlpha = 1;
  };
  drawSeries("rgba(142,163,154,.64)", 1, (index) => raw[index]);
  if (processed && processedOffset + count <= processed.smooth.length) drawSeries("#8ef0c5", 1.6, (index) => processed.smooth[processedOffset + index]);
  const peaks = analysis?.methods[state.selected.heart]?.peaks || [];
  const peakWindowStart = (processed?.raw.length || 0) - count;
  context.fillStyle = "#f18170";
  for (const peak of peaks) {
    if (peak < peakWindowStart) continue;
    const local = peak - peakWindowStart;
    const value = processed.smooth[peak];
    const x = local / (count - 1) * width;
    const y = height - 18 - (value - low) / range * (height - 36);
    context.beginPath();
    context.arc(x, y, 2.5, 0, Math.PI * 2);
    context.fill();
  }
  context.fillStyle = "rgba(210,231,220,.45)";
  context.font = "8px DM Mono, monospace";
  context.textAlign = "left";
  context.fillText(`${Math.round(low)} ADC`, 8, height - 8);
  context.textAlign = "right";
  context.fillText(`${Math.round(high)} ADC`, width - 8, 13);
}
function drawTrend() {
  const canvas = $("#trendCanvas");
  const target = canvasContext(canvas);
  if (!target) return;
  const { context, width, height } = target;
  context.fillStyle = "#07110f";
  context.fillRect(0, 0, width, height);
  drawGrid(context, width, height, 12, 3);
  const now = Date.now();
  const series = [
    { key: "heart", low: 40, high: 180, color: "#8ef0c5", label: "HR" },
    { key: "respiratory", low: 6, high: 30, color: "#d9ef71", label: "RR" }
  ];
  for (const definition of series) {
    context.beginPath();
    context.strokeStyle = definition.color;
    context.lineWidth = 1.5;
    let drawing = false;
    for (const point of state.trends) {
      const value = point[definition.key];
      if (!Number.isFinite(value)) {
        drawing = false;
        continue;
      }
      const x = width - clamp2((now - point.time) / 6e4, 0, 1) * width;
      const normalized = clamp2((value - definition.low) / (definition.high - definition.low), 0, 1);
      const y = height - 12 - normalized * (height - 24);
      if (!drawing) {
        context.moveTo(x, y);
        drawing = true;
      } else context.lineTo(x, y);
    }
    context.stroke();
  }
  context.font = "8px DM Mono, monospace";
  context.fillStyle = "#8ef0c5";
  context.fillText("HR 40\u2013180", 8, 13);
  context.fillStyle = "#d9ef71";
  context.fillText("RR 6\u201330", 78, 13);
}
function drawCharts() {
  drawWaveform();
  drawTrend();
}
function initializeRecording() {
  $("#recordButton").addEventListener("click", toggleRecording);
  $("#exportButton").addEventListener("click", exportSession);
  $("#markButton").addEventListener("click", () => {
    if (!state.recording) {
      showToast("Start recording before adding a marker.");
      return;
    }
    const marker = { at: (/* @__PURE__ */ new Date()).toISOString(), elapsedMs: Date.now() - state.recordingStart, posture: PROTOCOL[state.protocol.phase]?.posture || "unassigned", label: `Manual marker ${state.markers.length + 1}` };
    state.markers.push(marker);
    showToast(`${marker.label} added.`);
  });
}
function toggleRecording() {
  if (state.recording) return finalizeActiveRecording({
    message: "Recording stopped and finalized for export.",
    reasonCode: "manual_stop",
    detail: "Operator selected Stop recording"
  });
  if (!state.transport.serial) {
    showToast("Connect the UNO R4 before starting a recording.");
    return false;
  }
  try {
    metadataHandshake.assertFrameAllowed();
  } catch (error) {
    state.transport.error = error.message;
    updateTransportBadge();
    showToast(error.message);
    return false;
  }
  if (!currentAcquisitionReady()) {
    showToast("Wait for a live, fresh, evidence-clean UNO R4 stream before recording.");
    return false;
  }
  if (state.finalizedSegment?.session.length && !window.confirm(
    "Starting a new recording clears the finalized in-browser segment. Export it first if you need to keep it. Continue?"
  )) return false;
  startRecordingSegment(state, Date.now());
  recordingEvidence.reset();
  recordingEvidence.setDevice(state.device.id);
  $("#recordButton").textContent = "Stop recording";
  showToast("New recording segment started for all four raw channels.");
  renderRecording();
  return true;
}
function finalizeActiveRecording({ message = "", reasonCode = "manual_stop", detail = "" } = {}) {
  if (!state.recording) return false;
  finalizeRecordingSegment(state, Date.now(), recordingEvidence.snapshot(), { reasonCode, detail });
  if (state.protocol.running) {
    state.protocol.running = false;
    $("#protocolToggle").textContent = "Resume protocol";
  }
  $("#recordButton").textContent = "Start new session";
  renderRecording();
  if (message) showToast(message);
  return true;
}
function renderRecording() {
  const segment = state.finalizedSegment;
  const elapsed = state.recordingStart ? (state.recording ? Date.now() : state.recordingStop) - state.recordingStart : 0;
  const seconds = Math.floor(elapsed / 1e3);
  $("#recordingTime").textContent = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  $("#recordingDetail").textContent = state.recording ? `${state.session.length.toLocaleString()} raw samples \xB7 recording` : segment?.session.length ? `${segment.session.length.toLocaleString()} raw samples \xB7 finalized` : "Not recording";
  $("#exportButton").disabled = state.recording || !segment?.session.length;
}
function downloadBlob(filename, type, content) {
  const blob = new Blob([content], { type });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 1e3);
}
function exportSession() {
  const segment = state.finalizedSegment;
  if (state.recording || !segment?.session.length) return;
  const exported = createSessionExport({
    session: segment.session,
    device: state.device,
    selectedMethods: segment.selectedMethods,
    methodSelectionHistory: segment.methodSelectionHistory,
    summaries: segment.summaries,
    markers: segment.markers,
    heartMethods: HR_METHODS,
    respiratoryMethods: RR_METHODS,
    connectionDeclaration: state.connectionDeclaration,
    channelConnections: state.channelConnections,
    acquisitionEvidence: segment.acquisitionEvidence,
    recordingStartMs: segment.startedAt,
    recordingStopMs: segment.stoppedAt,
    termination: segment.termination
  });
  downloadBlob(exported.csv.filename, exported.csv.type, exported.csv.content);
  downloadBlob(exported.manifest.filename, exported.manifest.type, exported.manifest.content);
  showToast(`${segment.session.length.toLocaleString()} aligned samples and recording-scoped evidence exported.`);
}
function initializeProtocol() {
  renderProtocolSteps();
  $("#protocolToggle").addEventListener("click", () => {
    if (!state.protocol.running) {
      if (!state.recording && !toggleRecording()) return;
      state.protocol.running = true;
      state.protocol.startedAt = performance.now() - state.protocol.elapsed * 1e3;
      $("#protocolToggle").textContent = "Pause protocol";
    } else {
      state.protocol.running = false;
      $("#protocolToggle").textContent = "Resume protocol";
    }
  });
  $("#protocolReset").addEventListener("click", resetProtocol);
  $("#protocolNext").addEventListener("click", advanceProtocol);
  setInterval(updateProtocol, 200);
}
function resetProtocol() {
  state.protocol = { phase: 0, running: false, elapsed: 0, startedAt: 0 };
  $("#protocolToggle").textContent = "Start protocol";
  renderProtocol();
  showToast("Protocol reset to acclimation.");
}
function advanceProtocol() {
  if (state.protocol.phase < PROTOCOL.length - 1) {
    state.protocol.phase += 1;
    state.protocol.elapsed = 0;
    state.protocol.startedAt = performance.now();
    if (state.recording) {
      state.markers.push({ at: (/* @__PURE__ */ new Date()).toISOString(), elapsedMs: Date.now() - state.recordingStart, posture: PROTOCOL[state.protocol.phase].posture, label: `Protocol: ${PROTOCOL[state.protocol.phase].name}` });
    }
    renderProtocol();
    showToast(`${PROTOCOL[state.protocol.phase].name} phase ready.`);
  } else {
    state.protocol.running = false;
    state.protocol.elapsed = PROTOCOL[state.protocol.phase].seconds;
    $("#protocolToggle").textContent = "Protocol complete";
    renderProtocol();
  }
}
function updateProtocol() {
  if (state.protocol.running) {
    state.protocol.elapsed = (performance.now() - state.protocol.startedAt) / 1e3;
    if (state.protocol.elapsed >= PROTOCOL[state.protocol.phase].seconds) advanceProtocol();
  }
  renderProtocol();
  renderRecording();
}
function renderProtocolSteps() {
  $("#protocolSteps").innerHTML = PROTOCOL.map((phase, index) => `<li data-phase="${index}"><div><strong>${phase.name}</strong><span>${phase.kind === "SETTLE" ? "Signal stabilization" : "Continuous PPG + one-minute summaries"}</span></div><b>${String(Math.floor(phase.seconds / 60)).padStart(2, "0")}:${String(phase.seconds % 60).padStart(2, "0")}</b></li>`).join("");
}
function renderProtocol() {
  const phase = PROTOCOL[state.protocol.phase];
  const remaining = Math.max(0, Math.ceil(phase.seconds - state.protocol.elapsed));
  $("#protocolPhaseKind").textContent = phase.kind;
  $("#protocolClock").textContent = `${String(Math.floor(remaining / 60)).padStart(2, "0")}:${String(remaining % 60).padStart(2, "0")}`;
  $("#protocolPhaseName").textContent = phase.name;
  $("#protocolInstruction").textContent = phase.instruction;
  $("#protocolProgress").style.width = `${clamp2(state.protocol.elapsed / phase.seconds, 0, 1) * 100}%`;
  $$("[data-phase]", $("#protocolSteps")).forEach((item) => {
    const index = Number(item.dataset.phase);
    item.classList.toggle("active", index === state.protocol.phase);
    item.classList.toggle("complete", index < state.protocol.phase);
  });
}
function initializePaperResults() {
  const filters = $("#resultFilters");
  filters.innerHTML = Object.entries(PAPER_RESULTS).map(([id, posture], index) => `<button type="button" data-posture="${id}" class="${index === 1 ? "active" : ""}">${posture.label}</button>`).join("");
  $$("button", filters).forEach((button) => button.addEventListener("click", () => {
    $$("button", filters).forEach((candidate) => candidate.classList.toggle("active", candidate === button));
    renderPaperResults(button.dataset.posture);
  }));
  renderPaperResults("seated");
}
function renderPaperResults(postureId) {
  const posture = PAPER_RESULTS[postureId];
  $("#resultsBody").innerHTML = posture.rows.map((row) => `<tr class="${row[6] ? "best" : ""}"><td>${row[0]}</td><td>${row[1]}</td><td>${row[2]}</td><td>${row[3] === null ? "\u2014" : Number(row[3]).toFixed(2)} ${row[0] === "HR" ? "BPM" : "BrPM"}</td><td>${row[4]}</td><td>${row[5] === null ? "\u2014" : Number(row[5]).toFixed(2)}</td></tr>`).join("");
  let note = `HeartPy HR and RR could only be calculated for ${posture.heartPyN}/15 participants in this position.`;
  if (postureId === "walking") note += " Table 6 does not report the NeuroKit\u2013Vallat row.";
  if (postureId === "standing") note += " The standing prose repeats seated Charlton values; this table follows published Table 5.";
  if (postureId === "supine") note += " Table 3 bolds HeartPy\u2013Sarkar although HeartPy\u2013Vallat has a higher \u03C1; the authors selected Sarkar as the preferred balance.";
  if (postureId === "seated") note += " The conclusion names NeuroKit\u2013Sarkar, while the abstract and Table 4 identify NeuroKit\u2013Charlton at \u03C1 0.82; this dashboard follows Table 4.";
  $("#paperNote").textContent = note;
}
var serialPort = null;
var serialReader = null;
var serialLoopGeneration = 0;
async function disconnectSerial() {
  serialLoopGeneration += 1;
  if (state.recording) finalizeActiveRecording({
    message: "Serial stopped; recording segment finalized for export.",
    reasonCode: "disconnect",
    detail: state.transport.error || "Serial connection ended"
  });
  if (serialReader) {
    try {
      await serialReader.cancel();
    } catch {
    }
    serialReader.releaseLock();
    serialReader = null;
  }
  if (serialPort) {
    try {
      await serialPort.close();
    } catch {
    }
    serialPort = null;
  }
  state.transport.serial = false;
  state.transport.stream = false;
  state.transport.hz = 0;
  metadataHandshake.reset();
  streamHealth.reset();
  if (state.protocol.running) {
    state.protocol.running = false;
    $("#protocolToggle").textContent = "Resume protocol";
  }
  $("#connectButton").textContent = "Connect UNO R4";
  renderTransportEvidence();
  updateTransportBadge();
}
async function readSerial(generation) {
  const decoder = new TextDecoder();
  let pending = "";
  try {
    while (serialReader && generation === serialLoopGeneration) {
      const { value, done } = await serialReader.read();
      if (done) break;
      pending += decoder.decode(value, { stream: true });
      const lines = pending.split(/\r?\n/);
      pending = lines.pop() || "";
      for (const line of lines) parseLine(line);
    }
  } catch (error) {
    state.transport.error = error.message;
    showToast(`Serial stopped: ${error.message}`);
  } finally {
    if (generation === serialLoopGeneration) await disconnectSerial();
  }
}
async function connectSerial() {
  if (serialPort) {
    await disconnectSerial();
    return;
  }
  if (!("serial" in navigator)) {
    showToast("Web Serial requires desktop Chrome or Edge on localhost/HTTPS.");
    return;
  }
  if (state.finalizedSegment?.session.length && !window.confirm(
    "Connecting a device starts a new recording session and clears the current in-browser session. Export it first if you need to keep it. Continue?"
  )) return;
  try {
    serialPort = await navigator.serial.requestPort();
    await serialPort.open({ baudRate: 921600, bufferSize: 8192 });
    serialReader = serialPort.readable.getReader();
    resetRecordingSession(state);
    recordingEvidence.reset();
    resetAcquisitionSession(state, acquisitionEvidence, DEFAULT_DEVICE_PROFILE, SENSOR_INPUT_DECLARATION);
    metadataHandshake.reset();
    streamHealth.reset();
    state.transport.error = metadataHandshake.error;
    state.transport.serial = true;
    $("#recordButton").textContent = "Start recording";
    $("#protocolToggle").textContent = "Start protocol";
    $$(".channel[data-channel]").forEach((button) => {
      $("[data-channel-value]", button).textContent = "-- ADC";
      delete button.dataset.value;
    });
    delete document.body.dataset.channelValues;
    renderDeviceProfile();
    renderTransportEvidence();
    renderProtocol();
    renderAll();
    $("#connectButton").textContent = "Disconnect";
    updateTransportBadge();
    const generation = ++serialLoopGeneration;
    readSerial(generation);
  } catch (error) {
    state.transport.error = error.message;
    await disconnectSerial();
    showToast(`Could not open device: ${error.message}`);
  }
}
function selectChannel(index) {
  state.activeChannel = index;
  state.raw = state.channels[index];
  state.analysis = null;
  state.analysisAt = 0;
  state.lastPeakAbsolute = -1;
  $$(".channel[data-channel]").forEach((button) => button.classList.toggle("active", Number(button.dataset.channel) === index));
  $("#waveformChannel").textContent = state.transport.serial ? `CH${index + 1} / ${state.device.channelLabels[index]}` : `CH${index + 1} / waiting for device`;
  if (state.raw.length >= SAMPLE_RATE * 8) scheduleAnalysis();
  renderAll();
}
function initializeStream() {
  $("#connectButton").addEventListener("click", connectSerial);
  $$(".channel[data-channel]").forEach((button) => button.addEventListener("click", () => selectChannel(Number(button.dataset.channel))));
  if ("serial" in navigator) navigator.serial.addEventListener("disconnect", (event) => {
    if (isActiveSerialDisconnect(serialPort, event)) void disconnectSerial();
  });
  setInterval(checkStreamWatchdog, STREAM_WATCHDOG_INTERVAL_MS);
}
function animationLoop() {
  if ($("#panel-live").classList.contains("is-active")) drawCharts();
  requestAnimationFrame(animationLoop);
}
function initialize() {
  initializeTabs();
  initializeRecording();
  initializeProtocol();
  initializePaperResults();
  initializeStream();
  renderDeviceProfile();
  renderAll();
  animationLoop();
  window.addEventListener("resize", drawCharts);
}
initialize();
